/** Temas de ayuda · Compras y fiscal: compras, gastos, retenciones, reportes y libros. */
import { VE_TASA_2, type PermisoAyuda, type TemaAyuda } from '../tipos.ts';

/** Registrar, confirmar, pagar, corregir y anular compras (la dueña; el contador solo mira). */
const REGISTRA_COMPRAS: PermisoAyuda = { clave: 'compras.registrar', nivel: 'total' };
const REINTEGRA: PermisoAyuda = { clave: 'retenciones.reintegro-validar' };
const CORRIGE_RETENCIONES: PermisoAyuda = { clave: 'retenciones.corregir-comprobante' };
const GESTION: PermisoAyuda = { clave: 'reportes.sucursal' };
const FISCAL: PermisoAyuda = { clave: 'reportes.fiscal' };
const MANUALES: PermisoAyuda = { clave: 'libros.facturas-manuales' };

const SIN_SUCURSAL = { mensaje: 'Esta operación requiere operar una sucursal (usa el contexto de sucursal)', que_hacer: 'Estás en la consola global: elige la sucursal en la cabecera y vuelve a intentarlo.' };
const RECORTE = 'Si el rango trae demasiados renglones, el detalle muestra solo una parte y lo avisa en ámbar. Los totales siempre son del rango completo: acorta las fechas para ver el resto.';

export const TEMAS_FISCAL: TemaAyuda[] = [
  // ─── Compras ───────────────────────────────────────────────────────────────
  {
    id: 'compras',
    titulo: 'Compras y proveedores',
    rutas: ['/compras'],
    permiso: { clave: 'compras.registrar' },
    resumen: 'Las facturas de los proveedores: se registran, se confirman y se pagan. De aquí salen los comprobantes de retención de IVA e ISLR y los archivos de la declaración.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Cinco pestañas: **Compras** (la lista de facturas), **Órdenes de compra** (pedidos de cotización al proveedor), **Proveedores**, **Cuentas por pagar** y **Declaración (TXT / XLSX)**.',
          '**Nueva compra**, arriba a la derecha, abre el formulario para cargar una factura.',
          'Pulsa una compra de la lista para abrir su detalle: desde ahí se confirma, se numeran sus comprobantes, se paga, se corrige o se anula.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quién hace qué',
        items: [
          'La dueña registra, confirma, paga, corrige y anula.',
          'El contador ve todo y descarga la declaración, pero no tiene los botones para cambiar nada.',
          'Los demás roles no entran a Compras, salvo que la dueña les dé el permiso.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'La compra se carga cuando llega la factura del proveedor. Las tiendas no reciben compras.',
          'La compra queda en la sucursal que estás operando. Si estás en la consola global, elige primero la sucursal en la cabecera.',
          'Lo que tiene factura va por aquí: entra al libro de compras y lleva sus retenciones. Lo que no tiene factura va por **Gastos**.',
          'La mercancía que llega con una **nota de entrega** del proveedor, sin factura, también se carga aquí como **Nota de entrega (sin factura)**: entra al inventario y al costo, pero no al libro de compras, sin IVA ni retenciones. Para el ISLR es gasto no deducible.',
          'Una compra confirmada no se borra: se corrige (queda una versión nueva) o se anula, siempre con motivo.',
          'Los pagos a proveedores no salen de la caja ni cambian su cierre.',
          'Lo fiscal de la compra (base, IVA, total y retenciones) se guarda en bolívares: los de la factura si vino en Bs, o a la tasa BCV de la compra si vino en dólares.',
          'El resumen muestra cada importe en $ y en Bs (base, IVA, total, retenciones y neto). Con la factura en Bs, los bolívares van primero, para cotejarlos con la factura física.',
        ],
      },
    ],
    mensajes: [SIN_SUCURSAL],
    manual: '22',
    relacionados: ['compras-editor', 'gastos', 'libros', 'reportes'],
    claves: ['proveedor', 'factura de compra', 'retención de IVA', 'ISLR', 'cuentas por pagar', 'comprobante de retención', 'TXT', 'XML'],
  },
  {
    id: 'compras.lista',
    titulo: 'Pestaña Compras',
    padre: 'compras',
    resumen: 'Todas las compras de la sucursal con su total, sus retenciones, lo que falta por pagar y su estado.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra cada fila',
        items: [
          '**N° interno**, **Proveedor** (con su RIF), **Factura / nota** (la factura con su número de control; la nota de entrega dice «Nota …» con la marca **Sin factura** y si fue de contado o a crédito) y **Fecha** (debajo, el período del libro en que entra; la nota no va al libro).',
          '**Total**, **Ret. IVA**, **Ret. ISLR** y **Neto / saldo**: lo que se le paga al proveedor y lo que todavía se le debe.',
          '**Estado**: **Borrador** (guardada, sin ningún efecto), **Confirmada** o **Anulada**.',
          'Marcas junto al estado: **v2**, **v3** (se corrigió), **regenerar TXT** (cambió después de bajar el TXT del IVA), **regenerar XML** (cambió después de bajar el XML del ISLR) y **retención provisional** (tiene comprobantes sin numerar).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Buscar',
        items: [
          'El filtro de estado elige **Todos los estados**, **Borradores**, **Confirmadas** o **Anuladas**. El de documento elige **Facturas y notas**, **Con factura** o **Sin factura (notas de entrega)**.',
          'El buscador encuentra por número de factura, número de control, proveedor o RIF.',
          'La lista va por fecha de factura, de la más reciente a la más vieja, 50 por página. Al pie dice cuántas hay y, si no caben, trae **Anterior** y **Siguiente**.',
        ],
      },
      { tipo: 'nota', texto: 'Pulsa una fila para abrir el detalle de la compra.' },
    ],
    manual: '22',
    relacionados: ['compras.detalle', 'compras-editor'],
    claves: ['lista de compras', 'borrador', 'buscar factura'],
  },
  {
    id: 'compras-ordenes',
    titulo: 'Órdenes de compra',
    resumen: 'Pedido de cotización o de mercancía al proveedor: productos y cantidades, sin precios. Propone lo que está bajo el mínimo, se imprime o se envía por correo y se recibe en una o varias entregas, cada una como una compra.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Hacer una orden',
        pasos: [
          'En **Compras**, pestaña **Órdenes de compra**, pulsa **Nueva orden de compra**.',
          'Elige el **Proveedor** y dónde **Debe llegar** (viene el almacén principal). Si el proveedor no está en la lista, elige **+ Nuevo proveedor** al final de la lista o pulsa **Nuevo**: llena sus datos, guarda y queda elegido en la orden.',
          'A la derecha, **Sugeridos por el mínimo** muestra lo que está por debajo del mínimo de la sucursal o de cada tienda y depósito, con lo que falta en cada uno. Marca lo que le pides a este proveedor y pulsa **Agregar los marcados**: entra con la cantidad sugerida.',
          'Agrega otros productos con el buscador. Cambia las cantidades y, si hace falta, escribe una **Nota** por renglón y las **Observaciones**.',
          'Pulsa **Crear orden de compra**. Toma su número (OC-A-000001).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Después',
        items: [
          '**Imprimir o PDF**: abre la hoja en carta; en el cuadro de impresión puedes guardarla como PDF.',
          '**Enviar por correo**: la manda al correo registrado del proveedor, con el pedido de que responda con su cotización. Si el proveedor no tiene correo, agrégalo en su ficha (Proveedores).',
          '**Pasar a compra con factura** o **Con nota de entrega**: cuando llega la mercancía, abre una compra nueva con el proveedor, el almacén y lo **pendiente** de cada producto; **Con nota de entrega** la abre como nota sin factura. Escribe lo que llegó (si vino una parte, solo esa parte), quita lo que no vino, escribe los costos y guarda. La orden descuenta lo recibido igual con factura o con nota.',
          'Si el proveedor entrega por partes, la orden queda **Recibida en parte** y muestra, por producto, lo pedido, lo recibido y lo pendiente, y la lista de entregas (la nota de entrega, con la marca **Sin factura**). Para la siguiente entrega pulsa **Recibir otra entrega con factura** o **Con nota de entrega**. Cuando llega todo, queda **Recibida completa**.',
          '**Cerrar lo pendiente**: si el resto no va a llegar. Pide el motivo; lo recibido queda y lo pendiente vuelve a salir en los sugeridos.',
          '**Anular**: solo sin entregas. Pide el motivo. Lo que pedía vuelve a salir en los sugeridos.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'La orden no mueve inventario, ni cuentas por pagar, ni libros: eso lo hace la compra.',
          'Lo que sigue pendiente en otra orden se descuenta de los sugeridos; lo que no pediste sigue saliendo como sugerido para otra orden.',
          'Se edita mientras no tenga entregas. Con una entrega, lo pedido ya no cambia: si falta algo, haz otra orden.',
          'Si se corrige o se anula una compra de la orden, la orden vuelve a contar lo recibido. Una compra en borrador ya cuenta como recibida; la mercancía entra al inventario al confirmarla.',
        ],
      },
    ],
    manual: '22.8',
    rutas: ['/compras/ordenes/nueva', '/compras/ordenes/:id'],
    relacionados: ['compras.lista', 'compras-editor'],
    claves: ['orden de compra', 'cotización al proveedor', 'proforma', 'sugeridos', 'mínimo', 'pedir mercancía', 'entrega parcial', 'pendiente', 'recibir con nota'],
  },
  {
    id: 'compras.detalle',
    titulo: 'Detalle de una compra',
    padre: 'compras',
    resumen: 'Todo lo de una compra: renglones, totales, retenciones, comprobantes, pagos y correcciones. Desde aquí se confirma, se numeran los comprobantes, se paga, se corrige y se anula.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Arriba: el proveedor, la factura y su control, las fechas, la moneda y la tasa, el almacén de ingreso y quién la confirmó. Una nota de entrega lleva la marca **Sin factura**, su condición (de contado o a crédito) y un aviso de que no va al libro de compras; sus totales no traen IVA ni retenciones.',
          'Los renglones con su IVA, su ISLR (con el código de concepto, o en ámbar «sin código de concepto») y el costo real por unidad de cada artículo.',
          '**Gastos al costo de los productos**: a qué artículos fue cada servicio con factura y cada gasto sin factura (con su **Gasto N°**). Quien ve costos ve además el **Costo real por unidad**.',
          'Los totales, cada uno en $ y en Bs (con la factura en Bs, los bolívares primero): base, IVA, total de la factura, retenciones y **Neto por pagar**, con lo pagado y el saldo.',
          '**Comprobantes de retención** (cada uno dice cuántas veces se imprimió), **Pagos** y **Correcciones**, cada corrección con su motivo.',
          'Cada pago sale con el nombre de su método y en su moneda: uno en Bs dice los bolívares y, al lado, su equivalente en $ y la tasa con que se llevó.',
          '**Factura del proveedor y otros archivos**: la foto o el PDF de la factura y otros soportes adjuntos, con quién los subió y cuándo.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Adjuntar la factura del proveedor',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'En el detalle de la compra, en **Factura del proveedor y otros archivos**, pulsa **Adjuntar archivo** (o el botón de la cámara en el teléfono).',
          'Elige la foto o el PDF, de hasta 3 MB. Se guarda al elegirlo y queda en la auditoría.',
          'Sirve para la compra en borrador, confirmada o anulada. Si se adjuntó mal, la dueña lo borra con su motivo.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Confirmar un borrador',
        permiso: REGISTRA_COMPRAS,
        pasos: ['Revisa los renglones y los totales.', 'En una factura, mira **Así queda en los libros fiscales**: la fila que tendrá en el libro de compras y, si ves costos, lo que entra al libro de inventario.', 'Pulsa **Confirmar compra**. El aviso dice cuántos comprobantes provisionales nacieron y el neto por pagar.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa al confirmar',
        items: [
          'Los artículos entran al almacén y se actualiza su costo real.',
          'Se calculan las retenciones y nacen sus comprobantes, sin número (provisionales).',
          'Queda la cuenta por pagar al proveedor.',
          'Los gastos sin factura de la compra pasan a **Gastos**, con el número de la compra.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Numerar los comprobantes',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'En una compra confirmada con comprobantes provisionales, pulsa **Numerar comprobantes**.',
          'Revisa el **Período (AAAAMM)**: viene con el mes en curso.',
          'Mira **Así queda en los libros fiscales**: en el mes de la compra, lo retenido entra en la fila de la factura; en un mes posterior, sale una fila «Retención» en el libro de ese mes. También dice la quincena del TXT del IVA y la fecha del XML del ISLR.',
          'Pulsa **Numerar**. Cada comprobante toma el siguiente número de ese mes.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Solo lo numerado se declara. Los comprobantes provisionales no entran al TXT, al XML ni al libro de compras. El período no puede ser futuro ni anterior al de la factura.' },
      {
        tipo: 'pasos',
        titulo: 'Imprimir un comprobante',
        pasos: [
          'Pulsa el botón de impresora junto al comprobante.',
          'Se abre en otra pestaña, en hoja carta horizontal, con los dos logos y la firma cargada por la dueña. Imprime en papel «Carta», orientación horizontal.',
          'El provisional sale sin número y con la marca PROVISIONAL, para entregárselo al proveedor.',
          'El papel no lleva CORREGIDO ni REIMPRESIÓN: cada impresión se cuenta en el sistema y queda en la auditoría.',
          'En el de ISLR, el concepto sale con su nombre oficial («055 · Servicios y ejecución de obras (persona jurídica domiciliada)»).',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Pagar al proveedor',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'Pulsa **Registrar pago** (sale solo si la compra tiene saldo).',
          'Elige el **Método**: Transferencia (Bs), Pago móvil (Bs), Zelle ($), Efectivo $ o Efectivo Bs.',
          'El **Monto $** viene con el saldo. Puedes pagar una parte, nunca más del saldo.',
          'Escribe la **Referencia**: es obligatoria salvo en efectivo.',
          'Pulsa **Confirmar pago**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Corregir o anular',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          '**Corregir** (compra confirmada) o **Editar borrador** abre la compra en el formulario. La corrección pide motivo y deja una versión nueva.',
          '**Anular** abre el **Motivo de anulación**. Escríbelo y pulsa **Confirmar anulación**. Sirve también para un borrador: queda anulado sin haber tenido ningún efecto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al anular',
        items: [
          'No se anula una compra que ya tiene pagos.',
          'El costo de sus artículos vuelve al de su compra confirmada más reciente o, si no hay otra, al que tenían antes.',
          'Se anulan con ella sus servicios cargados al costo y sus gastos sin factura (los pendientes quedan rechazados). Cada gasto deja su propio registro en la auditoría.',
          'Si ya se había bajado el TXT del IVA de ese período, queda marcada **regenerar TXT**; si ya se había bajado el XML del ISLR, **regenerar XML**.',
          'Si parte de su mercancía ya se vendió o se entregó y anularla dejaría el almacén por debajo de lo que salió, el sistema no la anula y dice el producto, el almacén y lo que hay.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Anular una compra no se deshace. Si solo hay que cambiar un dato, usa **Corregir**.' },
    ],
    mensajes: [
      { mensaje: 'La compra tiene pagos registrados: no se puede anular', que_hacer: 'Ya se le pagó algo al proveedor: corrige la compra en vez de anularla.' },
      { mensaje: '…: parte de lo que trajo esta compra ya salió. En … hay … y anularla sacaría …', que_hacer: 'Esa mercancía ya se vendió o se entregó. Si la factura tiene un error, corrígela en vez de anularla.' },
      { mensaje: 'El pago ($ …) supera el saldo por pagar ($ …)', que_hacer: 'Baja el monto al saldo que muestra la compra.' },
      { mensaje: 'Indica la referencia bancaria del pago', que_hacer: 'Escribe la referencia de la transferencia, del pago móvil o del Zelle.' },
      { mensaje: 'No se numera un comprobante en un período que no ha llegado (…)', que_hacer: 'Usa el mes en curso o uno anterior.' },
      { mensaje: 'El período del comprobante no puede ser anterior al de la factura (…)', que_hacer: 'Usa el mes de la factura o uno posterior.' },
    ],
    manual: '22.3',
    relacionados: ['compras-editor', 'compras.declaracion', 'impresion', 'adjuntos'],
    claves: ['confirmar compra', 'numerar', 'provisional', 'pagar proveedor', 'anular compra', 'corregir compra', 'factura del proveedor', 'adjuntar'],
  },
  {
    id: 'compras.proveedores',
    titulo: 'Pestaña Proveedores',
    padre: 'compras',
    resumen: 'Los proveedores con sus datos fiscales, lo que se les retiene por defecto, cuántas compras tienen y cuánto se les debe.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**RIF** (con el tipo de persona), **Razón social** (con la dirección fiscal) y **Contacto**.',
          '**Retiene IVA** e **ISLR default**: los porcentajes que se proponen en sus compras.',
          '**Compras** y **CxP**: cuántas compras tiene y cuánto se le debe.',
          'Los proveedores inactivos salen en gris.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Crear o cambiar un proveedor',
        permiso: REGISTRA_COMPRAS,
        pasos: ['Pulsa **Nuevo proveedor**, o **Editar** en su fila.', 'Llena sus datos. El **?** de la ventana explica cada campo.', 'Pulsa **Guardar**.'],
      },
      { tipo: 'nota', texto: 'Para buscar, escribe el RIF o la razón social. También puedes crear un proveedor al cargar la compra, con el botón **Nuevo** junto al campo **Proveedor**.' },
    ],
    manual: '22.1',
    relacionados: ['compras.proveedor'],
    claves: ['proveedor', 'RIF', 'razón social'],
  },
  {
    id: 'compras.proveedor',
    titulo: 'Nuevo o editar proveedor',
    padre: 'compras',
    permiso: REGISTRA_COMPRAS,
    resumen: 'Los datos fiscales del proveedor, que salen en el comprobante de retención, y lo que se le retiene por defecto.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Llenar los datos',
        pasos: [
          '**RIF**: elige el prefijo y escribe el número sin guiones, con su dígito final. Aquí no se admite la cédula sola.',
          '**Razón social** y **Dirección fiscal**: tal como salen en su RIF. Van al comprobante de retención.',
          '**Teléfono**, **Correo** y **Notas**: opcionales.',
          '**Tipo de contribuyente**: Ordinario o Especial.',
          '**Retención de IVA que le aplicamos**: 75%, 100% o No retiene.',
          '**ISLR por defecto**: Sin ISLR, 1%, 2%, 3% o 5%. Con un porcentaje, sus renglones nuevos vienen con ISLR marcado.',
          '**Tipo de persona**: Según el RIF, Natural o Jurídica.',
          'Pulsa **Guardar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Con **Según el RIF**, los RIF que empiezan por V, E o P son de persona natural.',
          'A la persona natural el ISLR se le retiene con sustraendo, y su comprobante lo muestra.',
          'Al editar sale la casilla **Activo**. Un proveedor inactivo no aparece al buscarlo en una compra nueva.',
        ],
      },
    ],
    mensajes: [{ mensaje: 'RIF/CI inválido: elige el prefijo y escribe de 7 a 10 dígitos sin guiones', que_hacer: 'Prefijo en el desplegable y solo números, con el dígito final.' }],
    manual: '22.1',
    claves: ['nuevo proveedor', 'sustraendo', 'persona natural', 'retención 75'],
  },
  {
    id: 'compras.cuentas-por-pagar',
    titulo: 'Pestaña Cuentas por pagar',
    padre: 'compras',
    resumen: 'Las compras confirmadas que todavía tienen saldo, con su neto y lo que falta pagar.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un pago',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'Pulsa **Ver / pagar** en la compra.',
          'En el detalle, pulsa **Registrar pago**.',
          'Elige el **Método**, revisa el **Monto $** (viene con el saldo) y escribe la **Referencia** si no es efectivo.',
          'Pulsa **Confirmar pago**. Cuando el saldo llega a cero, la compra sale de esta lista.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Los pagos a proveedores no salen de la caja. Un pago en efectivo lo hace la administración y queda como registro.',
          'Si al confirmar sale un error de conexión, pulsa **Confirmar pago** otra vez sin cambiar nada: el pago no se registra dos veces.',
          'La lista trae 100 compras por página: al pie dice cuántas hay y, si no caben, trae **Anterior** y **Siguiente**.',
          'El saldo por proveedor y su factura más antigua están en **Reportes**, pestaña **Cuentas por pagar**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'El pago ($ …) supera el saldo por pagar ($ …)', que_hacer: 'Baja el monto al saldo de la compra.' },
      { mensaje: 'Indica la referencia bancaria del pago', que_hacer: 'Escribe la referencia: solo el efectivo va sin ella.' },
    ],
    manual: '22.6',
    relacionados: ['compras.detalle', 'reportes.cxp'],
    claves: ['pagar proveedor', 'deuda', 'abono a proveedor'],
  },
  {
    id: 'compras.declaracion',
    titulo: 'Pestaña Declaración (TXT / XLSX)',
    padre: 'compras',
    resumen: 'Los archivos de las retenciones para subirlos al portal del SENIAT: el TXT del IVA de cada quincena, el XML del ISLR del mes y un Excel con todo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Descargar la declaración',
        pasos: [
          'Escribe el **Período (AAAAMM)**: viene con el mes en curso.',
          'Pulsa **TXT IVA 1.ª quincena (1 al 15)** o **TXT IVA 2.ª quincena (16 al fin)**: el IVA retenido se declara por quincena. El aviso dice cuántos comprobantes trae y el total retenido en Bs.',
          'Pulsa **XML de ISLR** para la relación de retenciones de ISLR.',
          'Pulsa **Descargar XLSX** para un Excel con las retenciones de IVA, las de ISLR y el libro de compras del período.',
          'Sube los archivos a mano en el portal.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Solo entran los comprobantes numerados en ese período: los provisionales no. Antes de declarar, numera los que falten en el detalle de cada compra.' },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El TXT del IVA, el XML del ISLR y las retenciones del Excel son de toda la empresa, aunque los baje alguien operando una sucursal: hay un solo agente de retención y una sola numeración de comprobantes. El libro de compras sigue siendo por sucursal.',
          'El TXT va con el formato del SENIAT: RIF de 10 caracteres sin guiones y montos en bolívares a la tasa de cada compra.',
          'Cada retención va en la quincena de su fecha: la fecha fiscal de la compra o, si el comprobante se numeró en un mes posterior, el día en que se numeró. Esa misma fecha va en el XML del ISLR.',
          'Una quincena sin retenciones baja igual, con la línea en ceros que pide el SENIAT para declararla.',
          'Si una compra cambia o se anula después de bajar el TXT, queda marcada **regenerar TXT**; después de bajar el XML, **regenerar XML**. Cambiarla de mes solo la marca si tiene esa retención, antes o después del cambio.',
          'Vuelve a descargar ese archivo: la marca se quita en las que salieron en él y también en las que ya no traen un comprobante numerado que declarar (anuladas, corregidas que lo perdieron, o del período sin ninguno numerado). En el TXT, las de la quincena que bajaste.',
          'El sistema no se conecta con el SENIAT: solo prepara los archivos.',
        ],
      },
    ],
    mensajes: [{ mensaje: 'Faltan códigos de concepto de ISLR en: …', que_hacer: 'El XML necesita el concepto en cada renglón con ISLR: corrige esas compras y elige su **Concepto (para el XML)**.' }],
    manual: '22.7',
    relacionados: ['compras.detalle', 'libros.compras'],
    claves: ['declaración', 'TXT', 'XML ISLR', 'SENIAT', 'portal'],
  },

  // ─── Registrar o corregir una compra ───────────────────────────────────────
  {
    id: 'compras-editor',
    titulo: 'Registrar o corregir una compra',
    rutas: ['/compras/nueva', '/compras/:id/editar'],
    permiso: { clave: 'compras.registrar' },
    resumen: 'La factura del proveedor tal como llegó: proveedor, fechas, artículos y servicios con su IVA e ISLR. A la derecha ves el cálculo mientras escribes. Lo que no tiene factura va aparte.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar una compra',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'En **Compras**, pulsa **Nueva compra**.',
          'En **Proveedor**, escribe el RIF o la razón social y elígelo. Si no existe, créalo con **Nuevo**.',
          'Escribe el **N° factura**, el **N° control**, la **Fecha factura** y la **Fecha del período fiscal**.',
          'Revisa el **Almacén de ingreso** (viene el principal de la sucursal) y elige la **Moneda de la factura**: $ (divisa) o Bs.',
          'Si hace falta, escribe la **Tasa BCV (vacío = la del día de la factura)** y el **Descuento %** de la factura.',
          'Agrega los renglones con los botones **Artículo** y **Servicio**.',
          'Si hubo gastos sin factura (caleta, camión, comida de la cuadrilla), agrégalos con **Gasto sin factura** y repártelos.',
          'Pulsa **Guardar borrador** (no tiene ningún efecto todavía) o **Guardar y confirmar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Nota de entrega del proveedor (sin factura)',
        items: [
          'Arriba, en **Documento del proveedor**, elige **Nota de entrega (sin factura)** si la mercancía llegó con una nota y no con factura.',
          'Escribe el **N° de la nota** y la **Fecha de la nota**. No lleva número de control ni fecha del período fiscal: no entra al libro de compras.',
          'Solo productos (**Artículo**), con cantidad y costo **sin IVA**. Lo que no tiene factura (flete informal, caleta) va en **Gastos sin factura de esta compra**, como siempre.',
          '**Condición de pago**: **Crédito** (queda en cuentas por pagar) o **Contado** (elige **Cómo se pagó** y escribe la **Referencia del pago**; en efectivo es opcional). La de contado queda pagada al confirmarla, por todo el total.',
          'Al confirmar entra al inventario y al costo como cualquier compra, sin retenciones ni comprobantes, y queda marcada **Sin factura** (para el ISLR, gasto no deducible).',
          'Confirmada, ya no cambia a factura ni de contado a crédito. La de contado tiene su pago: no se anula ni se corrige por debajo de lo pagado.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Los renglones de la factura',
        items: [
          '**Artículo**: busca el producto. Entra al almacén y actualiza su costo.',
          '**Servicio** (flete, depósito): escribe la descripción. No mueve inventario.',
          'Por renglón: **Cant.**, el **Costo** en la moneda de la factura y la casilla **gravado** (lleva IVA). Desmárcala si es exento.',
          '**ISLR**: marca la casilla y elige el porcentaje, la base y el **Concepto (para el XML)**. Elegir el concepto propone el porcentaje. Con la base vacía, se retiene sobre todo el renglón.',
          'Solo salen los conceptos del tipo de persona del proveedor (natural o jurídica).',
          'La papelera quita el renglón.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Servicios que son costo de la mercancía',
        items: [
          'Si la compra trae artículos, marca **Cargar al costo de los artículos de esta compra** en el servicio (por ejemplo, el flete de la misma factura).',
          'Si la compra es solo de servicios, responde **¿Este servicio es costo de algún producto?** **No**: es un gasto operativo con factura y resta en la ganancia real. **Sí**: se reparte entre productos o entre lo que trajo otra compra.',
          'Una compra solo de servicios no necesita almacén: deja **(solo servicios)**.',
          'Cómo repartir se explica en su propia ayuda, con el **?** junto al título del reparto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Gastos sin factura de esta compra',
        items: [
          'Concepto, **Monto $**, **A quién se pagó** y **Forma de pago**. Siempre son **De la tienda**.',
          'No van en la factura, ni en el libro, ni en la cuenta por pagar. Al confirmar se guardan en **Gastos**, amarrados a la compra.',
          'Solo se anotan si la compra trae artículos, porque se reparten entre ellos. Sin artículos, el gasto se registra en **Gastos**.',
          'Anotarlos requiere también el permiso de Gastos.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las dos fechas',
        items: [
          '**Fecha factura**: la que trae la factura del proveedor. No puede ser posterior a hoy.',
          '**Fecha del período fiscal**: con esta fecha entra la compra al libro de compras. Viene con hoy. No puede ser posterior a hoy ni anterior a la fecha de la factura.',
          'Orden cronológico: una compra no se confirma con una fecha del período fiscal anterior a la de la última compra confirmada de la sucursal. El aviso dice cuál es y desde qué fecha se puede.',
          'Si una fecha no vale, sale un aviso en rojo encima de los renglones. Corrígela antes de guardar.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El cálculo de la derecha',
        items: [
          'Base gravada y exenta, descuento, IVA, total de la factura (también en Bs, con la tasa), retención de IVA, ISLR por porcentaje (con el sustraendo si es persona natural) y **Neto por pagar**.',
          'Con la **Tasa BCV** vacía se usa la última BCV registrada hasta el final del día de la **Fecha factura** (para una factura de hoy, la vigente). Si no hay ninguna de ese día ni de antes, el sistema pide escribirla.',
          'Es la misma regla al crear, al editar un borrador y al corregir: lo que ves a la derecha es lo que se guarda. Al editar, la casilla viene con la tasa guardada; si cambias la **Fecha factura**, debajo avisa que es la tasa de la fecha anterior: vacíala para tomar la BCV de la fecha nueva.',
          'Quien ve costos ve además el **Costo real resultante por unidad** de cada artículo. Si la compra trae dos renglones del mismo producto, su costo de factura es el promedio de los dos por unidades.',
          'Mientras falte algo para guardar, un aviso en ámbar dice qué.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Así queda en los libros fiscales',
        items: [
          'En una compra con factura, debajo del cálculo y encima de los botones, la vista previa muestra cómo va a salir la compra en los libros. Solo muestra: no guarda nada.',
          '**Libro de compras de** (el mes): la fila con la fecha, el RIF, el proveedor, el N° de factura y de control, y en Bs el total con IVA, el exento, la base, el IVA, lo retenido y el N° de comprobante. Entra en el mes de la **Fecha del período fiscal** (o de la **Fecha factura**, si está vacía).',
          'Lo retenido entra al libro del mes en que numeres los comprobantes. Hasta entonces la fila sale con la marca «retención provisional» y lo retenido en cero.',
          'Quien ve costos ve además el **Libro de inventario (real y fiscal)**: cada artículo con sus unidades y su valor en Bs, en el mes en que se confirme la compra.',
          'Con la nota de entrega no sale: no entra al libro de compras.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Corregir una compra confirmada',
        permiso: REGISTRA_COMPRAS,
        pasos: [
          'En el detalle de la compra, pulsa **Corregir**.',
          'Cambia lo que haga falta y escribe el **Motivo de la corrección**.',
          'Pulsa **Guardar corrección**. Queda una versión nueva y el comprobante se rehace con su mismo número.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al corregir',
        items: [
          'Cada renglón viene tal como se escribió en la factura: el costo sin el **Descuento %** (se vuelve a aplicar al guardar) y la base de ISLR escrita a mano.',
          'Si ya se había bajado el TXT del IVA de ese período, la compra queda marcada **regenerar TXT**; si ya se había bajado el XML del ISLR, **regenerar XML**. El aviso al guardar dice cuál volver a bajar.',
          'Si la mercancía ya se vendió o se entregó, se mueve solo la diferencia de unidades: corregir el número de la factura no falla. Si la corrección dejaría menos de lo que ya salió, el sistema lo dice con el producto y el almacén.',
          'Los gastos sin factura ya guardados salen con su **Gasto N°**: su reparto se cambia en **Gastos**. Los nuevos que anotes se guardan con la corrección.',
          'Un artículo con gastos sin factura no se puede quitar hasta cambiar el reparto de ese gasto en **Gastos**.',
          'Si quitas un artículo, su costo vuelve al de su compra confirmada más reciente.',
          'El nuevo neto no puede quedar por debajo de lo ya pagado.',
          'Al editar un borrador solo está **Guardar borrador**: se confirma después en su detalle, con **Confirmar compra**.',
        ],
      },
      { tipo: 'nota', texto: 'Si al guardar una compra nueva sale un error de conexión, pulsa el mismo botón otra vez sin cambiar nada: la compra no se registra dos veces.' },
    ],
    mensajes: [
      { mensaje: 'La factura … de este proveedor ya está registrada (…)', que_hacer: 'Es una compra repetida: búscala en la lista.' },
      { mensaje: 'La nota de entrega … de este proveedor ya está registrada (…)', que_hacer: 'Es una nota repetida: búscala en la lista con el filtro **Sin factura (notas de entrega)**.' },
      { mensaje: 'Una nota de entrega lleva solo productos…', que_hacer: 'Quita los servicios: un flete con factura va en una compra con factura; lo que no tiene factura, en **Gastos sin factura de esta compra**.' },
      { mensaje: 'Indica la referencia bancaria del pago de contado', que_hacer: 'La nota de contado por transferencia, pago móvil o Zelle necesita la **Referencia del pago**.' },
      { mensaje: 'La compra … se confirmó como nota de entrega: no pasa a factura…', que_hacer: 'Si se cargó con el documento equivocado, anúlala (si no tiene pagos) y regístrala de nuevo.' },
      { mensaje: 'La factura no puede tener fecha posterior a hoy…', que_hacer: 'Revisa el día, el mes y el año.' },
      { mensaje: 'La fecha del período fiscal no puede ser posterior a hoy · … no puede ser anterior a la fecha de la factura', que_hacer: 'Corrige la **Fecha del período fiscal**.' },
      { mensaje: 'Ya hay una compra cargada con fecha del período fiscal …: esta no puede quedar con una fecha anterior', que_hacer: 'Usa esa fecha o una posterior: el libro de compras va en orden.' },
      { mensaje: 'Indica el almacén de ingreso de los artículos', que_hacer: 'La compra trae artículos: elige el **Almacén de ingreso**.' },
      { mensaje: 'La base ISLR de … supera el monto de la línea …', que_hacer: 'La base no puede ser mayor que el renglón: corrígela o déjala vacía.' },
      { mensaje: 'El concepto … no corresponde a …', que_hacer: 'El concepto tiene que ser del tipo de persona del proveedor y del mismo porcentaje del renglón: elige otro.' },
      { mensaje: '… tiene asignado el Gasto N° … de esta compra: cambia primero su reparto en Gastos y luego quítalo de la compra', que_hacer: 'En **Gastos**, cambia el reparto de ese gasto y vuelve a corregir la compra.' },
      { mensaje: 'El nuevo neto ($ …) es menor que lo ya pagado al proveedor ($ …)', que_hacer: 'La corrección no puede dejar la compra por debajo de lo pagado: revisa los montos.' },
      { mensaje: 'Guardada como borrador, pero no se pudo confirmar', que_hacer: 'Quedó el borrador. Lee el motivo, pulsa **Editar borrador** si hay que cambiar algo y luego **Confirmar compra**.' },
      { mensaje: 'Vuelve a bajar el TXT del IVA del período: ya se había bajado.', que_hacer: 'Vuelve a descargar el TXT en **Declaración (TXT / XLSX)**.' },
      { mensaje: 'Vuelve a bajar el XML del ISLR del período: ya se había bajado.', que_hacer: 'Vuelve a descargar el XML en **Declaración (TXT / XLSX)**. Si el aviso nombra los dos, baja los dos.' },
      { mensaje: 'No hay tasa BCV registrada del … ni de antes: escribe en «Tasa BCV» la de la fecha de la factura', que_hacer: 'La factura es de antes de la primera tasa registrada: escribe en **Tasa BCV (vacío = la del día de la factura)** la BCV de ese día.' },
      { mensaje: '…: parte de lo que trajo esta compra ya salió. En … hay … y esta corrección sacaría …', que_hacer: 'Revisa la cantidad: no puede quedar menos de lo que ya salió.' },
      SIN_SUCURSAL,
    ],
    manual: '22.2',
    relacionados: ['compras', 'compras-editor.reparto', 'compras.proveedor', 'gastos'],
    claves: ['nueva compra', 'cargar factura', 'fecha fiscal', 'concepto ISLR', 'flete', 'gasto sin factura', 'descuento', 'tasa BCV', 'nota de entrega', 'sin factura', 'compra de contado', 'vista previa', 'libro de compras'],
  },
  {
    id: 'compras-editor.reparto',
    titulo: 'Reparto de gastos al costo',
    padre: 'compras-editor',
    resumen: 'Escribes cuánto de cada gasto va a cada artículo. El costo real de un artículo es su costo de factura más su parte de los gastos, dividida entre sus unidades.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'En una compra con artículos',
        pasos: [
          'Marca **Cargar al costo de los artículos de esta compra** en el servicio, o agrega un **Gasto sin factura**.',
          'Debajo aparece la tabla **Reparto de gastos al costo**: una fila por artículo y una columna por gasto.',
          'Escribe en cada casilla cuánto de ese gasto va a ese artículo. Las casillas abren vacías: el sistema no propone nada.',
          'Mira el pie de cada columna: **Gasto**, **Asignado** y **Falta** (o **Sobra**). Tiene que decir **Cuadra**.',
          'A la derecha ves el **Gasto / unidad** y el **Costo real** que resulta para cada artículo.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'En una compra solo de servicios',
        pasos: [
          'Responde **Sí** a **¿Este servicio es costo de algún producto?**',
          'En **Repartir entre:** elige **Productos** (búscalos por nombre o código) o **Lo que trajo una compra** (elige una compra confirmada y salen sus artículos).',
          'Escribe el **Monto $** de cada producto. Las **Unidades** vienen de la existencia o de la compra: puedes cambiarlas, nunca a cero.',
          'Abajo ves el gasto, lo asignado y lo que falta. Tiene que decir **Cuadra**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Cada gasto tiene que cuadrar al centavo: montos positivos y con dos decimales como máximo.',
          'Mientras un gasto no cuadre, no se activan **Guardar borrador** ni **Guardar y confirmar**.',
          'El gasto por unidad sale con cuatro decimales, para que no se pierdan los centavos.',
          'Cada gasto suma una sola vez al costo, venga de **Compras** o de **Gastos**.',
          'Las ventas ya hechas conservan su costo. Cuando llega otra compra del mismo producto, su costo real pasa a ser esa factura más sus propios gastos.',
        ],
      },
    ],
    manual: '22.5',
    relacionados: ['compras-editor', 'gastos.reparto'],
    claves: ['costo real', 'prorratear', 'repartir gasto', 'flete', 'caleta', 'cuadra al centavo'],
  },

  // ─── Gastos ────────────────────────────────────────────────────────────────
  {
    id: 'gastos',
    titulo: 'Gastos',
    rutas: ['/gastos'],
    permiso: { clave: 'gastos.registrar' },
    resumen: 'Lo que sale sin factura (caleta, camión, comida de la cuadrilla) y los gastos generales, por tienda y categoría. Lo que tiene factura fiscal se registra en **Compras**.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Filtros: el estado (**Todos**, **Pendientes**, **Aprobados**, **Rechazados** o **Anulados**), **Asociados a productos** o **Gastos generales**, y el buscador (descripción, a quién se pagó, soporte o categoría).',
          'La dueña tiene además el filtro de clasificación: **Solo de la tienda** o **Solo no de la tienda**.',
          '**Reporte** muestra los totales entre dos fechas. **Categorías** (solo la dueña) arregla la lista de categorías. **Nuevo gasto** registra uno.',
          'Cada fila: número y fecha, categoría y clasificación, concepto, a quién se pagó, de qué compra viene, a qué productos va (con lo que toca por unidad), el método de pago, el monto en $ y en Bs, el estado y quién lo registró.',
        ],
      },
      { tipo: 'nota', texto: 'Junto a los bolívares de cada fila ves con qué tasa se guardaron. Los demás usuarios con el permiso de gastos no ven la tasa.', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: 'Los botones de cada fila',
        items: [
          '**Aprobar** y **Rechazar**: en un gasto **Pendiente de aprobación**.',
          '**Editar**: en un gasto pendiente que no nació en una compra.',
          'Ícono de flechas (**Cambiar reparto**): en un gasto aprobado, cambia a qué productos va.',
          'Ícono de copia (**Duplicar**): repite un gasto aprobado con fecha de hoy, con el comentario «Duplicado del Gasto N° … del …». No sale en los gastos de una compra (nacidos en ella o ligados a ella): sería el mismo gasto de esa compra.',
          'Ícono de anular: solo la dueña, en un gasto aprobado.',
          'Ícono del clip (**Archivos del gasto**): abre **Recibo y soportes** para ver o adjuntar la foto del recibo o el PDF del soporte. El número al lado dice cuántos tiene.',
        ],
      },
      { tipo: 'nota', texto: 'La lista trae 50 gastos por página. Al pie dice cuántos hay y, si no caben, trae **Anterior** y **Siguiente**.' },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Los gastos no salen de la caja. Un gasto en efectivo lo paga la administración y no cambia ningún cierre.',
          'Se guarda en dólares y en bolívares con la tasa del día, que queda fija en ese gasto.',
          'Un gasto nunca entra en los libros, en las retenciones ni en la declaración. Si es de productos, suma a su costo real; si es general, resta en la ganancia real.',
          'Los gastos **No de la tienda** solo los registra y los ve la dueña.',
          'Los gastos sin factura anotados en una compra llegan aquí solos, con el número de la compra.',
          'El gasto queda en la sucursal que estás operando. Su fecha no puede ser posterior a hoy.',
          'Al instalar no hay umbral: el gasto queda aprobado al registrarlo. Si la dueña fija un umbral en **Configuración** (**Parámetros del sistema**), lo que lo pase queda **Pendiente de aprobación**.',
        ],
      },
      { tipo: 'nota', texto: 'Los bolívares del gasto salen a la **tasa 2** vigente, que queda fija. La fila te dice con qué tasa se guardó; los registrados antes de ese cambio dicen «tasa BCV».', permiso: VE_TASA_2 },
      {
        tipo: 'pasos',
        titulo: 'Aprobar o rechazar un gasto pendiente',
        pasos: ['Filtra por **Pendientes**.', 'Revisa el gasto y pulsa **Aprobar** o **Rechazar**.', 'Lo resuelve la dueña u otra persona con el permiso de gastos, nunca quien lo registró.'],
      },
    ],
    mensajes: [
      SIN_SUCURSAL,
      { mensaje: 'Solo se anula un gasto aprobado: este está pendiente (se rechaza)', que_hacer: 'Un gasto pendiente no se anula: pulsa **Rechazar**.' },
      { mensaje: 'Este gasto está ligado a la compra …: duplicarlo repetiría el mismo gasto de esa compra. Si es otro gasto, regístralo con «Nuevo gasto».', que_hacer: 'Registra el otro gasto con **Nuevo gasto**.' },
    ],
    manual: '23',
    relacionados: ['gastos.nuevo', 'compras', 'compras-editor.reparto', 'adjuntos'],
    claves: ['gasto', 'caleta', 'sin factura', 'gasto general', 'categoría', 'umbral', 'tasa 2', 'duplicar', 'recibo', 'archivos del gasto'],
  },
  {
    id: 'gastos.nuevo',
    titulo: 'Nuevo gasto',
    padre: 'gastos',
    resumen: 'Registrar lo que se pagó sin factura fiscal. Se guarda en $ y en Bs con la tasa del día, y no sale de la caja.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un gasto',
        pasos: [
          'Pulsa **Nuevo gasto**.',
          'Si el gasto tiene factura fiscal, marca **Tiene factura fiscal**: el sistema te manda a **Compras** con **Ir a Compras**.',
          'Llena **Fecha**, **Categoría**, **Concepto**, **A quién se pagó**, **Método de pago**, **Moneda** y **Monto**. La **Fecha** no puede ser posterior a hoy. Con el permiso de gastos delegado, va desde el primer día del mes anterior; la dueña, sin límite.',
          'La dueña elige además la **Clasificación**: **De la tienda** o **No de la tienda**.',
          'Si tienes el recibo o la nota, escribe su número en **Soporte (N° recibo / nota)**.',
          'Responde **¿Este gasto es de algún producto?**',
          'Pulsa **Guardar**.',
          'Para guardar la foto del recibo o el PDF del soporte, pulsa después el clip en la fila del gasto (**Archivos del gasto**) y **Adjuntar archivo**.',
        ],
      },
      { tipo: 'nota', texto: 'Los bolívares salen a la tasa 2 vigente y quedan fijos en el gasto. La ventana te lo dice así; a los demás, «con la tasa del día».', permiso: VE_TASA_2 },
      {
        tipo: 'lista',
        titulo: '¿Este gasto es de algún producto?',
        items: [
          '**No**: gasto general. Resta en la ganancia real de la consola y sale en el reporte de gastos. No toca ningún producto.',
          '**Sí**: elige los productos (**Productos**, por nombre o código) o **Lo que trajo una compra**. Escribe el **Monto $** de cada uno y entre cuántas **Unidades** se divide. Ves en vivo lo asignado, lo que falta y el gasto por unidad.',
          'Un gasto de productos va en dólares: la **Moneda** queda en $.',
          'Un gasto **No de la tienda** es siempre general: no se puede repartir entre productos.',
        ],
      },
      { tipo: 'nota', texto: '**Guardar** no se activa hasta responder la pregunta y, si es de productos, hasta que el reparto cuadre al centavo.' },
      {
        tipo: 'pasos',
        titulo: 'Editar un gasto pendiente',
        pasos: [
          'En un gasto **Pendiente de aprobación**, pulsa **Editar**.',
          'Corrige los datos y pulsa **Guardar**. La categoría tiene que estar activa, como al registrarlo. Si el monto nuevo ya no pasa del umbral de aprobación, el gasto queda aprobado al guardar.',
          'Un gasto aprobado ya no se edita: si quedó mal, la dueña lo anula y se registra de nuevo.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'No se pudo guardar el gasto', que_hacer: 'Lee el detalle del aviso. Si fue un corte de conexión, pulsa **Guardar** otra vez sin cambiar nada: el gasto no se registra dos veces.' },
      { mensaje: 'La fecha del gasto no puede ser posterior a hoy (…): escribiste …', que_hacer: 'Corrige la **Fecha**: un gasto no puede tener fecha futura.' },
      { mensaje: 'Ese gasto ya está registrado: Gasto N° … del …, «…» por $ …, ligado a la compra …. No se crea otro.', que_hacer: 'Ese concepto y ese monto ya existen para esa compra: usa el que ya está.' },
      { mensaje: 'Gasto de $ … supera el umbral ($ …): pendiente de aprobación', que_hacer: 'Quedó registrado, pero espera que lo apruebe la dueña u otra persona con el permiso.' },
      { mensaje: 'La categoría … está inactiva: elige otra', que_hacer: 'Elige otra, o pídele a la dueña que la active en **Categorías**. Vale al registrar y al editar un gasto pendiente.' },
    ],
    manual: '23.1',
    relacionados: ['gastos.reparto', 'compras-editor'],
    claves: ['registrar gasto', 'soporte', 'recibo', 'no de la tienda', 'gasto de producto'],
  },
  {
    id: 'gastos.reparto',
    titulo: 'Cambiar el reparto del gasto',
    padre: 'gastos',
    resumen: 'Cambia a qué productos va un gasto aprobado y cuánto a cada uno. El monto no cambia y el costo real se recalcula.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar el reparto',
        pasos: [
          'En un gasto aprobado, pulsa el ícono de flechas.',
          'Responde **¿Este gasto es de algún producto?** con **Sí** o **No, gasto general**.',
          'Si es de productos, ajusta los productos, los montos y las unidades hasta que diga **Cuadra**.',
          'Escribe el **Motivo del cambio**.',
          'Pulsa **Guardar reparto**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'El reparto anterior queda en el histórico. Las ventas ya hechas conservan su costo.',
          'Un gasto que nació en una compra se reparte solo entre los artículos de esa compra: no puede volverse gasto general.',
          'Un gasto **No de la tienda** no va al costo de los productos.',
          'Si el monto quedó mal, no se arregla aquí: la dueña anula el gasto y se registra de nuevo.',
        ],
      },
    ],
    mensajes: [{ mensaje: 'Este gasto tiene fracciones de centavo en dólares: anúlalo y regístralo de nuevo para asociarlo a productos', que_hacer: 'Pídele a la dueña que lo anule y regístralo otra vez en dólares.' }],
    manual: '23.2',
    relacionados: ['compras-editor.reparto'],
    claves: ['cambiar reparto', 'costo real', 'recalcular costo'],
  },
  {
    id: 'gastos.anular',
    titulo: 'Anular un gasto',
    padre: 'gastos',
    resumen: 'La dueña anula un gasto aprobado que se tecleó mal o se registró dos veces. No se borra: queda tachado en la lista, con su motivo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Anular',
        pasos: [
          'En la fila del gasto, pulsa el ícono de anular (al pasar el cursor dice «Anular este gasto»).',
          'Revisa el resumen del gasto y escribe el **Motivo de la anulación**.',
          'Pulsa el botón rojo **Anular gasto**, que muestra el monto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa después',
        items: [
          'El gasto sigue en la lista, tachado y marcado **Anulado**, con quién lo anuló, cuándo y por qué.',
          'Deja de sumar en los totales y en el reporte.',
          'Si iba al costo de algún producto, ese aporte se revierte y queda en el histórico de la ficha.',
          'Para verlos, filtra por **Anulados**.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'No se puede deshacer: si hacía falta, se registra de nuevo con los datos correctos. Solo la dueña anula, aunque le haya dado a otra persona el permiso de gastos. Un gasto pendiente no se anula: se rechaza.' },
    ],
    manual: '23.3',
    claves: ['anular gasto', 'gasto repetido', 'borrar gasto'],
  },
  {
    id: 'gastos.reporte',
    titulo: 'Reporte de gastos',
    padre: 'gastos',
    resumen: 'Los gastos aprobados entre dos fechas, por categoría, por sucursal y por método de pago.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Ver el reporte',
        pasos: ['Pulsa **Reporte**.', 'Elige **Desde** y **Hasta** (viene el mes en curso).', 'Para cerrarlo, pulsa **Ocultar reporte**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Por categoría**, con el total del período y cuánto fue asociado a productos y cuánto a gastos generales.',
          'La dueña ve aparte el total **De la tienda** y el **No de la tienda**.',
          '**Por sucursal**, para comparar tiendas, y **Por método**, con cuántos gastos siguen pendientes.',
          'Los anulados no suman.',
        ],
      },
    ],
    manual: '23.2',
    claves: ['reporte de gastos', 'total de gastos', 'por categoría'],
  },

  // ─── Retenciones de clientes ───────────────────────────────────────────────
  {
    id: 'retenciones',
    titulo: 'Retenciones de clientes',
    rutas: ['/retenciones'],
    permiso: { clave: 'retenciones-clientes.cargar' },
    resumen: 'El seguimiento del IVA que retienen los clientes contribuyentes especiales (75 %) y de las retenciones municipales: qué comprobante falta, cuál cargar, qué reintegrar y qué revisar.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Las pestañas',
        items: [
          '**Por recibir**: facturas cobradas con Base + 25% IVA. El cliente debe el comprobante.',
          '**Por cargar**: facturas de Pago completo. Cuando el cliente trae el comprobante, se carga aquí.',
          '**Reintegros pendientes**: comprobantes ya cargados de facturas pagadas completas. Falta devolverle al cliente lo retenido.',
          '**Revisar**: retenciones que el contador debe confirmar.',
          '**Historial**: las recibidas, las reintegradas y las que quedaron sin efecto.',
          '**Control de reintegros**: para la administración, la dueña y el contador.',
          'Cada pestaña dice cuántas retenciones tiene.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo se usa',
        items: [
          'El filtro elige **IVA y municipal**, **Solo IVA** o **Solo municipal**. El buscador encuentra por factura, cliente, RIF o comprobante.',
          'Cada fila: la factura, el cliente, la retención (tipo, porcentaje y modalidad), la base y el monto en Bs (y en $), el comprobante con el mes del libro, el estado y los días que lleva.',
          'Una retención pendiente con más de 30 días sale en rojo. Arriba, una marca dice cuántas hay, y se avisa a los administradores y al contador.',
          'El botón del clip de cada fila abre sus archivos: la **Imagen del comprobante** y, si tiene reintegro, el **Comprobante del pago del reintegro**. El número al lado dice cuántos tiene.',
          'Cada pestaña trae 50 filas: las pendientes, las más antiguas; el **Historial**, las más recientes. Si hay más, al pie lo dice con el total: usa el buscador o el filtro de tipo.',
          'Los mensajes dicen los estados como la pantalla: «por cargar» y «sin efecto».',
          '**Retención de una factura sin retención**: para el comprobante de una factura que se emitió sin retención.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quién hace qué',
        items: [
          'Cargar comprobantes y adjuntar su imagen: el cajero, el administrador, el contador y la dueña.',
          'Reintegrar y adjuntar el comprobante del pago: el administrador y la dueña. La caja lo ve en el **Historial**.',
          'Mandar a revisar, resolver la revisión y corregir comprobantes: el contador y la dueña.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'La retención entra al libro de ventas del mes en que se recibió el comprobante, no del mes de la factura.' },
    ],
    mensajes: [{ mensaje: '… retención(es) superan 30 días: administradores y contador notificados', que_hacer: 'Sale al abrir la pantalla. Pídeles el comprobante a esos clientes o haz los reintegros pendientes.' }],
    manual: '21',
    relacionados: ['retenciones.comprobante', 'libros.ventas', 'adjuntos'],
    claves: ['retención', 'comprobante de retención', 'contribuyente especial', 'IVA 75', 'municipal', 'reintegro', 'saldo a favor', 'imagen del comprobante'],
  },
  {
    id: 'retenciones.por-recibir',
    titulo: 'Pestaña Por recibir',
    padre: 'retenciones',
    resumen: 'Facturas que el cliente pagó con Base + 25% IVA: lo retenido quedó como deuda hasta que traiga el comprobante.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cuando llega el comprobante',
        pasos: ['Busca la factura.', 'Pulsa **Cargar comprobante** y llena sus datos.', 'Al cargarlo se salda lo que el cliente debía por la retención, y pasa al **Historial** como **Recibida**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: ['La lista va de la más vieja a la más nueva y trae hasta 50.', 'Las que llevan más de 30 días salen en rojo.'],
      },
    ],
    manual: '21.1',
    relacionados: ['retenciones.comprobante'],
    claves: ['base más 25', 'comprobante pendiente'],
  },
  {
    id: 'retenciones.por-cargar',
    titulo: 'Pestaña Por cargar',
    padre: 'retenciones',
    resumen: 'Facturas que el cliente pagó completas. Cuando traiga el comprobante, se carga aquí.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar el comprobante',
        pasos: ['Pulsa **Cargar comprobante** en la retención.', 'Llena los datos y pulsa **Cargar**.', 'Pasa a **Reintegros pendientes**: falta devolverle al cliente lo retenido.'],
      },
      { tipo: 'nota', texto: 'Las que llevan más de 30 días salen en rojo.' },
    ],
    manual: '21.1',
    relacionados: ['retenciones.comprobante', 'retenciones.reintegros'],
    claves: ['pago completo', 'cargar comprobante'],
  },
  {
    id: 'retenciones.reintegros',
    titulo: 'Pestaña Reintegros pendientes',
    padre: 'retenciones',
    resumen: 'Comprobantes ya cargados de facturas pagadas completas. Falta devolverle al cliente lo que se le retuvo.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Reintegrar',
        permiso: REINTEGRA,
        pasos: ['Pulsa **Reintegrar** en la retención.', 'Revisa cuánto hay que devolver y elige el método.', 'Pulsa **Reintegrar**: pasa al **Historial** como **Reintegrada**.'],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Si la factura tuvo notas de crédito, la fila dice cuánto queda por reintegrar: el resto ya volvió con la nota.',
          'Solo reintegran el administrador y la dueña. Los demás ven la lista.',
          'Un reintegro con más de 30 días sin hacerse sale en rojo.',
          'Un reintegro que quedó programado trae **Ejecutar programado** y también **Reintegrar**, para hacerlo con otro método. Si lo programado ya no procede (a posteriori en efectivo), solo sale **Reintegrar**.',
        ],
      },
    ],
    manual: '21.3',
    relacionados: ['retenciones.reintegrar'],
    claves: ['reintegrar', 'devolver retención'],
  },
  {
    id: 'retenciones.revisar',
    titulo: 'Revisar retenciones',
    padre: 'retenciones',
    resumen: 'Las retenciones que el contador debe confirmar: las que alguien mandó a revisar y las de facturas que una nota de crédito dejó en cero antes de llegar el comprobante.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Mandar a revisar',
        permiso: CORRIGE_RETENCIONES,
        pasos: [
          'En la retención, pulsa **Mandar a revisar**.',
          'Escribe en **Qué hay que revisar** lo que hay que arreglar.',
          'Pulsa **Mandar a revisar**: queda en la pestaña **Revisar** hasta que se resuelva.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Resolver una revisión',
        permiso: CORRIGE_RETENCIONES,
        pasos: [
          'En **Revisar**, pulsa **Resolver revisión**.',
          'Si hace falta, escribe el **Monto corregido ($, opcional)**: solo hacia abajo, nunca más de lo que corresponde a la factura.',
          'Si la factura quedó en cero y el cliente ya no va a retener, marca **Dejarla sin efecto**. Solo sale si todavía no tiene comprobante.',
          'Escribe las **Observaciones** y pulsa **Marcar revisada**. La retención vuelve a la pestaña que le toca.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Una retención en **Revisar** sin comprobante admite **Cargar comprobante**. Si lo carga quien no corrige comprobantes, sigue en **Revisar** y el contador recibe el aviso.',
          'Si el monto cambia, la administración recibe el aviso: cambia lo que se le reintegra o se le cobra al cliente.',
          'No se mandan a revisar las ya reintegradas.',
          'Una retención con comprobante existe y se declara: no se deja sin efecto.',
        ],
      },
    ],
    mensajes: [{ mensaje: 'La retención … de … no puede pasar de $ … (su base por su porcentaje)', que_hacer: 'El monto corregido es mayor que lo que corresponde: escribe uno menor.' }],
    manual: '21.5',
    relacionados: ['retenciones.comprobante'],
    claves: ['revisar', 'resolver revisión', 'sin efecto', 'contador'],
  },
  {
    id: 'retenciones.historial',
    titulo: 'Pestaña Historial',
    padre: 'retenciones',
    resumen: 'Las retenciones ya terminadas: **Recibida**, **Reintegrada** y **Sin efecto**, de la más nueva a la más vieja.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'El comprobante, su fecha, el monto en Bs, el día en que se recibió y el mes del libro.',
          'Si el comprobante se corrigió, el ícono de reloj muestra sus versiones anteriores.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La columna Pago',
        items: [
          'En una reintegrada dice cómo se pagó el reintegro (o «Sin reintegro: ya devuelto con la nota de crédito»), la referencia, la fecha, el monto y quién lo validó.',
          'Si se adjuntó el comprobante del pago, sale **Comprobante del pago (…)**: púlsalo para verlo. Si no, dice «sin comprobante adjunto».',
          'Sirve para responderle al cliente cuando pregunta por su reintegro: la caja lo ve sin poder cambiarlo.',
          '**Enviar por correo** le manda al cliente el pago del reintegro con su comprobante adjunto, a su correo registrado.',
        ],
      },
      { tipo: 'nota', texto: 'Si falta el comprobante del pago, la columna trae **Adjuntar el comprobante**.', permiso: REINTEGRA },
      { tipo: 'nota', texto: 'El contador y la dueña pueden **Corregir** un comprobante o **Mandar a revisar** una retención recibida.', permiso: CORRIGE_RETENCIONES },
    ],
    manual: '21.1',
    relacionados: ['correo', 'adjuntos', 'retenciones.reintegrar'],
    claves: ['historial de retenciones', 'versiones', 'recibida', 'reintegrada', 'pago del reintegro', 'comprobante del pago', 'columna pago'],
  },
  {
    id: 'retenciones.control',
    titulo: 'Control de reintegros',
    padre: 'retenciones',
    resumen: 'Control interno de los comprobantes que siguen pendientes de reintegro: quién los cargó, cuándo y cuánto falta devolver.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Arriba, el total pendiente de reintegro.',
          'Cada fila: la factura (con la marca «a posteriori» si se cargó después), el cliente, el comprobante con su fecha y su monto en Bs, quién lo cargó y cuándo, el monto y los días que lleva.',
        ],
      },
      { tipo: 'nota', texto: 'Lo ven la administración, la dueña y el contador.' },
    ],
    manual: '21.1',
    relacionados: ['retenciones.reintegrar'],
    claves: ['control de reintegros', 'quién cargó'],
  },
  {
    id: 'retenciones.comprobante',
    titulo: 'Cargar o corregir un comprobante',
    padre: 'retenciones',
    resumen: 'Los datos del comprobante de retención que trajo el cliente, en bolívares y tal como vienen en el papel. El día en que lo recibiste decide el mes del libro de ventas.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar un comprobante',
        pasos: [
          'Pulsa **Cargar comprobante** en la retención.',
          'Escribe el **N° comprobante (AAAAMMNNNNNNNN)**: 14 dígitos (año, mes y número). El AAAAMM va del mes de la factura al mes del comprobante: un número de un mes futuro o anterior a la factura se rechaza.',
          'Escribe la **Fecha del comprobante**.',
          'Escribe el **Monto retenido (Bs)** tal como viene en el comprobante. Viene con lo calculado y se admite hasta 1 Bs de diferencia.',
          '**Recibido el**: el día en que lo recibiste (viene hoy). Su mes es el del libro de ventas.',
          'Si tienes la foto o el PDF del comprobante, en **Imagen del comprobante** pulsa **Adjuntar archivo** (o la cámara en el teléfono). Se guarda al elegirlo. Es opcional.',
          'Pulsa **Cargar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas de fechas',
        items: [
          'La recepción no puede ser futura.',
          'El comprobante no puede ser posterior a la recepción ni anterior a la factura.',
          'Un comprobante recibido hace más de un mes solo lo registran el contador o la dueña.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa después',
        items: [
          'Base + 25% IVA: se salda lo que el cliente debía por la retención y queda **Recibida**.',
          'Pago completo: queda **Reintegro pendiente**.',
          'Si estaba en **Revisar** y no corriges comprobantes, sigue en **Revisar** y el contador recibe el aviso.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Corregir un comprobante',
        permiso: CORRIGE_RETENCIONES,
        pasos: [
          'Pulsa **Corregir** en la retención.',
          'Cambia el número, la fecha, el monto o el día de recepción. Si cambias **Recibido el**, la retención pasa al libro de ese mes. Las reglas de fechas se revisan siempre, aunque no cambies **Recibido el**.',
          'Escribe el **Motivo de la corrección** y pulsa **Guardar corrección**.',
          'Queda una versión nueva y el libro muestra siempre la última. El ícono de reloj muestra las anteriores.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'El comprobante indica Bs … y la retención … es Bs … (tolerancia ±1 Bs)', que_hacer: 'Revisa el monto. Si el comprobante está mal, pásaselo al contador.' },
      { mensaje: 'Un comprobante recibido hace más de un mes lo registra el contador o la dueña', que_hacer: 'Ese libro ya pudo declararse: pásale el comprobante al contador.' },
      { mensaje: 'La fecha de recepción del comprobante no puede ser futura', que_hacer: 'Corrige **Recibido el**.' },
      { mensaje: 'La fecha del comprobante no puede ser posterior al día en que se recibió', que_hacer: 'Revisa la **Fecha del comprobante** y **Recibido el**.' },
      { mensaje: 'La fecha del comprobante no puede ser anterior a la de la factura (…)', que_hacer: 'Revisa la **Fecha del comprobante**.' },
      { mensaje: 'El comprobante … ya fue cargado para … (…)', que_hacer: 'Ese número ya está cargado en otra factura del cliente: revisa el número.' },
    ],
    manual: '21.2',
    relacionados: ['retenciones', 'libros.ventas'],
    claves: ['cargar comprobante', 'número de comprobante', 'corregir comprobante', 'recibido el'],
  },
  {
    id: 'retenciones.reintegrar',
    titulo: 'Validar y ejecutar reintegro',
    padre: 'retenciones',
    permiso: REINTEGRA,
    resumen: 'Devolverle al cliente lo que se le retuvo en una factura que pagó completa, una vez cargado el comprobante.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Reintegrar',
        pasos: [
          'En **Reintegros pendientes**, pulsa **Reintegrar**.',
          'Mira **A reintegrar**: si la factura tuvo notas de crédito, ya descuenta lo que esas notas le devolvieron al cliente.',
          'Elige el **Método**: Abono a saldo a favor, Efectivo $, Efectivo Bs, Transferencia (Bs), Zelle ($) o Pago móvil (Bs).',
          'Si es transferencia, Zelle o pago móvil, escribe la **Referencia bancaria**.',
          'En **Comprobante del pago**, adjunta la captura de la transferencia o la foto del recibo. Se guarda al elegirlo; también se puede adjuntar después, desde la columna **Pago** del **Historial**.',
          'Pulsa **Reintegrar**.',
          'Para avisarle al cliente, en el **Historial** pulsa **Enviar por correo** en la columna **Pago**: le llega el pago con su comprobante adjunto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Si no queda nada por devolver, la retención se cierra sin mover dinero.',
          '**Abono a saldo a favor**: el cliente puede usarlo como pago de inmediato.',
          'Al terminar sale «Reintegro registrado». El reintegro en efectivo lo entrega la administración: no sale de la caja ni cambia su cierre.',
          'Un reintegro que quedó programado de antes trae el botón **Ejecutar programado**: pasa por las mismas reglas y tampoco sale de la caja. Esa fila trae también **Reintegrar**: la ventana dice qué método estaba programado y lo que elijas lo reemplaza.',
          'Mientras la factura tenga saldo por cobrar, lo retenido solo se puede dejar como saldo a favor.',
          'Una retención cargada a posteriori no se reintegra en efectivo: va por transferencia, pago móvil, Zelle o saldo a favor. Si quedó programada en efectivo, la fila solo ofrece **Reintegrar**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: '… todavía tiene $ … por cobrar: mientras deba, lo retenido se deja como saldo a favor del cliente (lo usa al pagar)', que_hacer: 'Elige **Abono a saldo a favor**. Si salió al pulsar **Ejecutar programado**, el mensaje sigue con «Usa «Reintegrar» y elige el abono a saldo a favor»: pulsa **Reintegrar**.' },
      { mensaje: 'Indica la referencia bancaria del reintegro', que_hacer: 'Escribe la referencia de la transferencia, del Zelle o del pago móvil. En un programado, el mensaje sigue con «: usa «Reintegrar» para escribirla»: pulsa **Reintegrar**.' },
      { mensaje: 'El reintegro de una retención cargada a posteriori se hace por transferencia o se deja como saldo a favor, no en efectivo', que_hacer: 'Elige transferencia, Zelle, pago móvil o abono a saldo a favor. En un programado, pulsa **Reintegrar** para elegirlo.' },
    ],
    manual: '21.3',
    claves: ['reintegro', 'devolver retención', 'saldo a favor'],
  },
  {
    id: 'retenciones.posterior',
    titulo: 'Retención de una factura que salió sin retención',
    padre: 'retenciones',
    resumen: 'Para cuando el cliente trae el comprobante de una factura que se emitió sin retención, aunque ya esté cobrada. La factura no cambia.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar la retención',
        pasos: [
          'Pulsa **Retención de una factura sin retención**.',
          'En **Buscar la factura**, escribe el número, el cliente o el RIF, y elige la factura. La lista muestra las 8 más recientes y, si hay más, lo dice con el total. Si te equivocaste, pulsa **Cambiar**.',
          'Elige la **Retención**: IVA o Municipal.',
          'Revisa el **% retenido**: el IVA va al 75 %; la municipal, al porcentaje del cliente o al general. Otro porcentaje solo lo indica la dueña.',
          'Escribe el **N° comprobante (AAAAMMNNNNNNNN)**, la **Fecha del comprobante** y **Recibido el**.',
          'El **Monto retenido (Bs)** viene lleno con el calculado: cámbialo solo si el comprobante dice otro (hasta 1 Bs de diferencia). Con la municipal al porcentaje general (la casilla del % vacía), escribe el monto del comprobante.',
          'Si hace falta, escribe **Observaciones (opcional)**.',
          'Pulsa **Registrar retención**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa después',
        items: [
          'La retención entra al libro del mes en que se recibió y queda un **reintegro pendiente**.',
          'Ese reintegro se paga por transferencia, pago móvil o Zelle, o se deja como saldo a favor. Nunca en efectivo.',
          'Si la factura tiene una nota de crédito, la retención va a **Revisar** y el contador confirma el monto.',
          'La dueña puede dejar al cliente marcado para sus próximas facturas con la casilla **Dejar al cliente marcado**. Si registra otra persona, la administración recibe el aviso para marcarlo en su ficha.',
          'La imagen del comprobante se adjunta después, con el clip de su fila.',
        ],
      },
    ],
    mensajes: [
      { mensaje: '… ya tiene su retención de IVA: carga el comprobante con «Cargar comprobante» en la bandeja …', que_hacer: 'Esa factura ya tiene la retención y le falta el comprobante: búscala en la pestaña que dice el mensaje (**Por recibir**, **Por cargar** o **Revisar**) y pulsa **Cargar comprobante**. Con la municipal dice «municipal».' },
      { mensaje: '… ya tiene su retención de IVA con el comprobante … cargado: si hay que cambiarlo, la dueña o el contador usa «Corregir»', que_hacer: 'Ya tiene su comprobante. Si está mal, la dueña o el contador lo cambia con **Corregir**.' },
      { mensaje: '… ya tiene su retención de IVA, pero quedó sin efecto: para cargarle el comprobante, la dueña o el contador la reabre con «Mandar a revisar» en el Historial de Retenciones y luego se carga desde la bandeja Revisar', que_hacer: 'Una retención **Sin efecto** no tiene **Cargar comprobante**: la dueña o el contador pulsa **Mandar a revisar** en el **Historial**, y después se carga en **Revisar**.' },
      { mensaje: 'Una factura a consumidor final no admite retención: el comprobante necesita el RIF del cliente', que_hacer: 'Esa factura no puede llevar retención.' },
      { mensaje: '… no tiene IVA: no hay qué retener', que_hacer: 'Revisa que sea la factura correcta.' },
      { mensaje: 'No hay un % de retención municipal configurado: la dueña lo fija en Configuración o en la ficha del cliente', que_hacer: 'Pídele a la dueña que fije el porcentaje.' },
      { mensaje: 'El comprobante indica Bs … y la retención … es Bs … (tolerancia ±1 Bs)', que_hacer: 'Revisa el monto contra el calculado.' },
    ],
    manual: '21.4',
    relacionados: ['retenciones.reintegrar'],
    claves: ['a posteriori', 'factura sin retención', 'retención tardía', 'marcar cliente especial'],
  },

  // ─── Reportes ──────────────────────────────────────────────────────────────
  {
    id: 'reportes',
    titulo: 'Reportes',
    rutas: ['/reportes'],
    permiso: { clave: 'reportes.fiscal' },
    resumen: 'Los reportes de gestión y los fiscales, cada uno en su pestaña, con su rango de fechas y su Excel.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué pestañas ves',
        items: [
          'La dueña: todas.',
          'El administrador: todas menos **Por tienda**.',
          'El contador: solo las fiscales, **Compras**, **Cuentas por pagar** e **IGTF**.',
          'El cajero y almacén no entran a Reportes.',
          'Se entra con cualquiera de los tres permisos de reportes (de la sucursal, fiscales o consolidado) y cada pestaña pide el suyo: si la dueña le da uno solo a alguien, ve las pestañas de ese permiso.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Ver un reporte',
        pasos: [
          'Elige la pestaña.',
          'Si entras sin sucursal fija (la dueña desde la consola global, o el contador), elige la **Sucursal**: una, o **Todas (consolidado)**.',
          'Ajusta **Desde** y **Hasta** (viene el mes en curso). Entre las dos fechas cabe hasta un año: para más, pide un año a la vez.',
          'Para bajarlo a Excel, pulsa **Exportar XLSX**: baja la pestaña que estás viendo, con los mismos filtros.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          '**Cuentas por cobrar** y **Cuentas por pagar** no llevan fechas: muestran lo pendiente hoy. **Estimación de compra** usa **Días de demanda**.',
          'Los importes a costo (valorizado, mermas, ajustes, último costo) salen en blanco o con una raya para quien no tiene el permiso de costos. El administrador ve cantidades.',
          RECORTE,
          'Cuando el detalle viene recortado, el Excel también lo dice, en una fila al final de esa hoja.',
          'Las fechas van en hora de Caracas, también en el Excel.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'La fecha final es anterior a la inicial', que_hacer: 'Corrige **Desde** y **Hasta**.' },
      { mensaje: 'Elige hasta un año (366 días) entre «Desde» y «Hasta»', que_hacer: 'Acorta el rango a un año o menos. La declaración es por quincena, así que nunca hace falta más.' },
    ],
    manual: '24',
    relacionados: ['libros', 'reportes.igtf'],
    claves: ['reporte', 'excel', 'XLSX', 'consolidado', 'ventas del mes'],
  },
  {
    id: 'reportes.ventas',
    titulo: 'Reporte de ventas',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Lo vendido y lo cobrado en el rango: documentos, ventas fiscales y no fiscales, y el detalle por método, tipo, caja y cajero.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Documentos**, **Ventas fiscales (FAC + ND − NC)**, **No fiscales (NOTA − CAM)** y **Total cobrado (sin el vuelto)**.',
          '**Por método de pago**: los cobros y abonos del rango, en su moneda y en dólares, con cuántas operaciones. Es lo recibido; si hubo vuelto, la fila **Vuelto entregado** lo resta antes del **Total cobrado**.',
          '**Por tipo de documento**, **Por caja**, **Por cajero** (de mayor a menor monto) y, en el consolidado, **Por sucursal**.',
          '**Documentos del rango**: fecha, documento, cliente, caja y cajero, total y estado del pago. El número abre el documento.',
        ],
      },
      { tipo: 'nota', texto: 'Si tienes tu propia caja abierta y sin declarar, lo recaudado en ella no sale en **Total cobrado**, **Por método de pago** ni **Por caja** hasta que declares tu conteo. La pantalla lo avisa. Los documentos y los totales vendidos sí la incluyen.' },
      { tipo: 'nota', texto: RECORTE },
    ],
    manual: '24',
    relacionados: ['documentos'],
    claves: ['ventas', 'cobrado', 'por cajero', 'por método'],
  },
  {
    id: 'reportes.tiendas',
    titulo: 'Reporte por tienda',
    padre: 'reportes',
    permiso: { clave: 'reportes.consolidado' },
    resumen: 'Solo la dueña: cuánto vendió cada tienda en el período y cuáles fueron los productos más vendidos.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Ventas del período**, **En bolívares**, **Cobrado** y **Documentos**.',
          '**Ventas por tienda**: documentos, **Facturado** (facturas y notas de débito, menos notas de crédito), **Notas** (notas de entrega, menos cambios), ventas en $ y en Bs, cobrado (sin el vuelto) y su producto más vendido.',
          '**Productos más vendidos**: los diez con más unidades entre todas las tiendas, con cuántas vendió cada una. Lo devuelto no cuenta.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: ['Muestra siempre todas las tiendas: no usa la **Sucursal** elegida.', 'Pulsar el nombre de una tienda la elige en **Sucursal**. Abre la pestaña **Ventas** para ver su reporte.'],
      },
    ],
    manual: '24',
    claves: ['por tienda', 'más vendidos', 'comparar tiendas'],
  },
  {
    id: 'reportes.inventario',
    titulo: 'Reporte de inventario',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Existencias valorizadas, lo pendiente por entregar, mermas, despieces y lo que está bajo el mínimo.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Existencias valorizadas (costo real)**, **Mermas / roturas del rango** y **Ubicaciones bajo o cerca del mínimo**.',
          'Si hay traslados en camino, sale además la tarjeta **En camino**: esa mercancía ya está sumada en el valorizado, a costo de la guía y en la tienda de origen hasta que llega.',
          '**Por almacén**: productos, unidades, valorizado y unidades pendientes por entregar.',
          '**Pendientes por entregar (antigüedad)**: cada documento con sus días y sus unidades. El número abre el documento.',
          '**Mermas, roturas y pérdidas**: las aprobadas, por su fecha de aprobación.',
          '**Despieces**: cajas rotas pasadas a piezas sueltas, láminas de vidrio pasadas a piezas con medida y productos con detalles que pasaron a su lista.',
          '**Mínimo por ubicación**: **Existencia**, **Disponible** y **Mínimo** de cada tienda o depósito. Bajo o cerca se decide con lo disponible (existencia menos lo vendido sin retirar).',
          '**Bajo mínimo (total de la sucursal)**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Filtros',
        items: [
          '**Ubicación** y **Producto** (código o nombre) filtran mermas, despieces y mínimos. Las existencias valorizadas y lo que va en camino son siempre de toda la sucursal.',
          'Las fechas de arriba filtran mermas y despieces.',
          RECORTE,
          'El aviso de recorte de los despieces sale solo si de verdad quedó alguno fuera (se muestran los 200 más recientes).',
        ],
      },
    ],
    manual: '24',
    relacionados: ['inventario'],
    claves: ['valorizado', 'mermas', 'despieces', 'mínimo', 'pendiente por entregar'],
  },
  {
    id: 'reportes.ajustes',
    titulo: 'Reporte de ajustes',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Un renglón por producto ajustado en el rango, con sus totales y su resumen por motivo.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Ajustes del rango**, **Pendientes de aprobación**, y lo que **Entró** y **Salió** a costo (solo aprobados).',
          '**Aprobados por motivo**: cuántos ajustes, unidades que entraron y salieron, y valor neto.',
          '**Detalle**: fecha, ajuste con su estado, ubicación, motivo (con la justificación), producto, cantidad, valor y quién lo propuso y quién lo resolvió.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Filtros: **Ubicación**, **Producto** y **Estado** (Todos, Pendientes, Aprobados o Rechazados).',
          'Va por la fecha en que se registró el ajuste. Incluye roturas, mermas, daños, diferencias de conteo y mermas de traslado. Los despieces van en **Inventario**.',
          'El número del ajuste abre la pantalla de **Ajustes**.',
          'El Excel trae además una hoja **Resumen** con los totales del período.',
          RECORTE,
        ],
      },
    ],
    manual: '24',
    relacionados: ['ajustes'],
    claves: ['ajustes de inventario', 'mermas', 'por motivo'],
  },
  {
    id: 'reportes.traslados',
    titulo: 'Reporte de traslados',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Un renglón por producto de cada guía: lo enviado, lo recibido y lo que faltó con su causa.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Guías del rango**, **En tránsito**, **Con diferencia** y **Unidades que faltaron**.',
          '**Por ruta**: guías, enviado, recibido y faltante por cada origen y destino.',
          '**Detalle**: fecha de despacho, guía con su estado, origen y destino (con vehículo y chofer), producto, enviado, recibido y faltante. La causa dice si quedó en el origen o fue merma, y «por revisar» si falta el visto bueno.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Filtros: **Ubicación** (como origen o como destino), **Producto** y **Diferencias** (Todas las guías, Con diferencia o Sin diferencia).',
          'Va por la fecha de despacho. El número abre la guía.',
          'Una tienda ve solo los traslados en que participa. Este reporte no muestra costos.',
          'El Excel trae además una hoja **Resumen** con los totales del período. El estado de cada guía va con su nombre, como en la pantalla («En tránsito», «Recibido en parte», «Recibido», «Cancelado»).',
          RECORTE,
        ],
      },
    ],
    manual: '24',
    relacionados: ['traslados'],
    claves: ['traslados', 'guías', 'faltante', 'diferencia'],
  },
  {
    id: 'reportes.cambios',
    titulo: 'Reporte de cambios y devoluciones',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Cada nota de crédito o cambio del rango: qué devolvió el cliente, qué se llevó y qué pasó con el crédito.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'El filtro **Cliente** busca por nombre o RIF.',
          'Tarjetas: **Cambios y devoluciones del rango** y **Crédito reconocido**.',
          'Cada fila: documento (con su origen), cliente, lo recibido (marca lo dañado), lo despachado, el crédito, la diferencia cobrada, lo que quedó a favor (saldo a favor o dinero por transferir) y el motivo.',
          'El cambio viene de una nota de entrega; la nota de crédito, de una factura.',
        ],
      },
      { tipo: 'nota', texto: RECORTE },
    ],
    manual: '24',
    relacionados: ['devoluciones'],
    claves: ['cambios', 'devoluciones', 'nota de crédito', 'crédito'],
  },
  {
    id: 'reportes.compras',
    titulo: 'Reporte de compras',
    padre: 'reportes',
    permiso: FISCAL,
    resumen: 'Lo comprado en el rango, lo retenido a los proveedores y el costo real de cada producto.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Total facturas**, **IVA retenido** e **ISLR retenido**, en dólares.',
          '**Por proveedor**: compras, total, retenciones y lo que se le debe.',
          '**Costos por producto**: costo de factura, gastos con factura (servicios cargados al costo), gastos sin factura (de **Gastos**), costo real, costo unitario promedio, margen contra la lista principal y proveedores.',
          '**Cuadre entre Compras, Gastos y costo de productos**: lo cargado al costo (servicios con factura más gastos sin factura) tiene que ser igual a lo asignado a productos más los gastos generales sin factura. Dice **Cuadra** o **No cuadra**.',
        ],
      },
      { tipo: 'nota', texto: 'Este reporte va en dólares. Lo que se declara está en el libro de compras, en bolívares.' },
    ],
    manual: '24',
    relacionados: ['compras', 'libros.compras'],
    claves: ['compras', 'costo real', 'margen', 'cuadre de costos', 'por proveedor'],
  },
  {
    id: 'reportes.estimacion',
    titulo: 'Estimación de compra',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Qué comprar según lo vendido en los últimos días, el mínimo y lo disponible, con el proveedor habitual.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Usarla',
        pasos: ['Escribe los **Días de demanda** (de 7 a 365; vienen 30). Se cuentan esos días con hoy incluido.', 'Revisa la columna **Sugerido**: las filas con algo que comprar salen resaltadas.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Demanda** de esos días y **Diaria**, **Disponible**, **Mínimo** y **Cobertura (d)**: para cuántos días alcanza lo disponible.',
          '**Sugerido**: la demanda de esos días más el mínimo, menos lo disponible.',
          '**Proveedor habitual** y **Último costo**.',
        ],
      },
    ],
    manual: '24',
    claves: ['qué comprar', 'sugerido de compra', 'demanda', 'reposición'],
  },
  {
    id: 'reportes.cxc',
    titulo: 'Cuentas por cobrar',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Lo que deben los clientes, para cobrar: primero quienes tienen deuda vencida.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Cuentas por cobrar** y **Vencido**.',
          '**Lista de cobranza**: cliente (abre su ficha), teléfono, documentos, total, vencido, el saldo por tramos (al día, de 1 a 30, de 31 a 60 y más de 60 días), días de atraso y saldo a favor. Quienes deben vencido salen en rojo.',
          '**Documentos por vencimiento**: cada documento con su saldo y sus días vencido. El número abre el documento.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: ['No lleva fechas: muestra lo pendiente hoy.', 'Al abrirla se generan los avisos de facturas vencidas que falten.', RECORTE],
      },
    ],
    manual: '24',
    relacionados: ['clientes'],
    claves: ['cobranza', 'deudores', 'vencido', 'cuentas por cobrar'],
  },
  {
    id: 'reportes.cxp',
    titulo: 'Cuentas por pagar',
    padre: 'reportes',
    permiso: FISCAL,
    resumen: 'Lo que se les debe a los proveedores, por proveedor y por compra.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjeta **Cuentas por pagar** con el total.',
          '**Por proveedor**: compras, saldo y factura más antigua.',
          '**Compras con saldo**: el número abre la compra para pagarla. Si son muchas, muestra las más antiguas y lo avisa; el total de arriba sí las suma todas.',
          'No lleva fechas: muestra lo pendiente hoy.',
        ],
      },
    ],
    manual: '24',
    relacionados: ['compras.cuentas-por-pagar'],
    claves: ['deuda con proveedores', 'cuentas por pagar'],
  },
  {
    id: 'reportes.cierres',
    titulo: 'Reporte de cierres',
    padre: 'reportes',
    permiso: GESTION,
    resumen: 'Las diferencias de arqueo por cajero y las sesiones de caja del rango, con sus retiros.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Diferencias de arqueo por cajero**: cierres, cuántos con diferencia y la diferencia acumulada. Arriba, el total del rango.',
          '**Sesiones**: caja y cajero, apertura, cierre, cobrado (sin el vuelto), retiros, egresos, diferencia y notas (la justificación y si se reabrió).',
          'Debajo de la diferencia, «entre métodos» suma las diferencias de cada método sin compensar faltantes con sobrantes.',
          'Va por la fecha de cierre de cada caja; una caja abierta, por la de apertura.',
        ],
      },
      { tipo: 'nota', texto: 'Tu propia caja abierta y sin declarar no muestra lo cobrado: dice «al declarar».' },
    ],
    manual: '24',
    relacionados: ['cierres'],
    claves: ['arqueo', 'diferencias', 'sesiones de caja', 'faltante'],
  },
  {
    id: 'reportes.igtf',
    titulo: 'Reporte de IGTF',
    padre: 'reportes',
    permiso: FISCAL,
    resumen: 'El IGTF percibido en el rango, por día y por cobro, para la declaración.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Tarjetas: **Cobros con IGTF**, **Base pagada en divisas**, **IGTF percibido (divisa)** e **IGTF en Bs (tasa BCV del cobro)**. **Cobros con IGTF** cuenta solo los cobros: si hay facturas manuales, su título las nombra aparte («(y … factura(s) manual(es))»).',
          'Arriba, las facturas con pago en divisas: base, IVA, total y lo pagado en divisas, en Bs. Cada factura cuenta una sola vez.',
          '**Por día**: con su quincena, operaciones, base e IGTF en divisa y en Bs.',
          '**Por quincena**: el subtotal de cada quincena (del 1 al 15 y del 16 al último día), que es como se declara.',
          '**Facturas manuales**: el IGTF escrito a mano en las facturas de talonario, solo en Bs. Ya está sumado en los totales, como en el libro de ventas.',
          '**Detalle por cobro**: fecha, documento y control, cliente, base, IVA y total de la factura en Bs, instrumento, porcentaje, base e IGTF en divisa, tasa BCV, lo pagado en divisas en Bs y el IGTF en Bs.',
          'Entran las facturas y notas de débito pagadas total o parcialmente en divisas (efectivo $, Zelle, USDT) o con saldo a favor que entró con abonos en divisas, el día en que se emiten o se cobran. Las notas de entrega no causan IGTF.',
          'En **Instrumento** sale «Saldo a favor (en divisas)» solo si ese cobro gastó esa parte del saldo; si además se pagó con otro método, salen los dos («Saldo a favor (en divisas), Efectivo $»). El saldo de una nota de crédito no sale: no paga IGTF.',
          'Los abonos y anticipos desde el 19/10/2026 no entran: no llevan IGTF hasta que pagan una factura. Los anticipos anteriores, que lo cobraron al recibirse, siguen saliendo como «ABONO».',
          'El Excel trae la hoja **Por quincena** y las facturas manuales en filas «FAC manual».',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Imprimir o guardar en PDF',
        pasos: ['Pulsa **Imprimir / PDF**.', 'Se abre el reporte en otra pestaña, con las fechas y la sucursal elegidas.', 'Imprímelo o guárdalo como PDF desde el cuadro de impresión.'],
      },
      { tipo: 'nota', texto: 'La hoja impresa trae lo mismo que la pantalla: cada cobro, las facturas manuales en filas «FAC manual …» con solo su IGTF en Bs (así la columna suma el total) y el subtotal **Por quincena**. El encabezado cuenta aparte los cobros, las facturas y las facturas manuales.' },
      { tipo: 'nota', texto: 'Con el mes completo, el total coincide con la columna **IGTF** del libro de ventas de ese mes.' },
    ],
    manual: '24',
    relacionados: ['libros.ventas', 'impresion'],
    claves: ['IGTF', 'divisas', 'quincena', 'declaración IGTF'],
  },

  // ─── Libros fiscales ───────────────────────────────────────────────────────
  {
    id: 'libros',
    titulo: 'Libros fiscales',
    rutas: ['/libros'],
    permiso: { clave: 'libros.ver' },
    resumen: 'El libro de ventas y el libro de compras de un mes, en bolívares y con las columnas del SENIAT, las facturas hechas a mano en el talonario y el libro de inventario de la empresa.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Pestañas: **Libro de ventas**, **Libro de compras**, **Facturas manuales** y, para la dueña y la contadora, **Libro de inventario**.',
          'Escribe el **Período (AAAAMM)**: viene el mes en curso. El mes va de 01 a 12; con uno que no existe, debajo lo dice y no se muestra ningún libro.',
          'Quien entra sin sucursal fija (la dueña desde la consola global, o el contador) elige la **Sucursal** en los libros de ventas y de compras: una, o **Todas (consolidado)**. Con una elegida, las filas y los totales son solo de esa tienda. El libro de inventario es siempre de toda la empresa.',
          '**Exportar XLSX oficial** baja a Excel el libro que estás viendo, sin las marcas de la pantalla. Desde **Facturas manuales** baja el libro de ventas, con su nombre (libro_ventas_AAAAMM.xlsx).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Quién hace qué',
        items: ['El contador y la dueña ven los libros y registran facturas manuales.', 'El administrador ve los libros sin cambiar nada, pero sí registra y anula facturas manuales.'],
      },
      {
        tipo: 'lista',
        titulo: 'Verificación',
        items: [
          'Arriba de cada libro, una franja verde dice que los totales cuadran al centavo contra los documentos del período.',
          'La verificación también cruza el libro con otros reportes que deben dar lo mismo: en el de ventas, el total de **IGTF** contra el reporte de IGTF del mes; en el de compras de toda la empresa (**Todas (consolidado)**), el IVA retenido contra los comprobantes de IVA del período (los del TXT) y el ISLR retenido contra los de ISLR (los del XML). Solo leen: no cambian ninguna cifra. Cada cruce sale debajo de la franja con sus dos montos. La franja verde quiere decir que también cuadran; si uno no da igual, la franja roja dice cuál y con qué montos.',
          'Si sale en rojo **¡NO CUADRA!**, no debería pasar: repórtalo antes de declarar.',
          '**PRELIMINAR**: el libro de ventas, mientras el período está en curso o hay cajas abiertas; el de compras, mientras el mes no termine o haya compras del mes en borrador (las cajas no lo cambian). Si no, **Período cerrado**. En el libro de ventas, la pantalla y el Excel dicen el motivo real: «el mes no ha terminado», «hay … caja(s) abierta(s) del período» o las dos cosas.',
          'En el libro de ventas, una marca roja avisa si hubo reaperturas de caja en el período.',
          'En el Excel, debajo de los totales, una fila dice si es PRELIMINAR (con el motivo: «No es el libro para declarar.») o «Libro definitivo del período.». En el de ventas, si hubo reaperturas de caja, otra fila lo dice. Al final, una fila dice si la verificación cuadra (y, si no, qué no cuadra), y debajo va una fila por cada cruce con sus dos montos.',
        ],
      },
    ],
    mensajes: [
      { mensaje: '¡NO CUADRA! Diferencia Bs … contra los documentos del período', que_hacer: 'Es un error del sistema: no declares con ese libro y repórtalo.' },
      { mensaje: 'Período AAAAMM: el mes va de 01 a 12 (por ejemplo, 202610)', que_hacer: 'Escribe el año y el mes con dos cifras, por ejemplo 202610.' },
    ],
    manual: '25',
    relacionados: ['retenciones', 'compras.declaracion', 'reportes.igtf'],
    claves: ['libro de ventas', 'libro de compras', 'SENIAT', 'período', 'preliminar', 'verificación', 'talonario'],
  },
  {
    id: 'libros.ventas',
    titulo: 'Libro de ventas',
    padre: 'libros',
    resumen: 'Las facturas, notas de crédito y notas de débito del mes, en bolívares: lo que se declara de las ventas.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué entra',
        items: [
          'Facturas y notas de débito suman; las notas de crédito restan y salen en rojo. Las notas de entrega y los cambios no entran.',
          'Columnas: fecha, RIF, razón social, tipo, número (abre el documento), control, total con IVA, exento, base, IVA, **IGTF**, IVA retenido con su comprobante y retención municipal.',
          'Las anuladas por hoja dañada salen en su fecha, con su número y su control, y todos los importes en cero.',
          'Las facturas de talonario manual salen con la marca **manual**.',
          'El RIF, el número y el control salen cada uno en una sola línea.',
          'El Excel trae la columna **Tasa BCV**. Al contador y a la dueña les trae además, al lado, la columna **Tasa aplicada**: la tasa con que cada documento calculó sus bolívares. Al administrador no le sale.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Retenciones e IGTF',
        items: [
          'El IVA retenido entra en el mes en que se recibió el comprobante. Una factura cuyo comprobante no ha llegado va con la retención en cero.',
          'Si el comprobante llega en otro mes, sale en ese mes como una fila propia marcada **Retención**. También el de una factura manual, según su **Comprobante recibido el**.',
          'En la fila de una factura manual, y en su fila **Retención**, la **Fecha comprobante** trae la fecha impresa en el comprobante, si se escribió. Las registradas antes del 24/10/2026 no la tienen y va vacía.',
          'Un comprobante corregido lleva la marca **corregido** y su versión: el libro muestra siempre la última.',
          'La columna **IGTF** de una factura suma sus cobros de ese mismo mes en divisas o con saldo a favor que entró con abonos en divisas.',
          'El IGTF percibido en otro mes sale en el mes del cobro, en una fila propia: **IGTF cobrado** (factura de otro período) o **IGTF anticipo** (solo los anticipos anteriores al 19/10/2026). Esas filas no suman ventas ni cuentan como documentos.',
          'Un abono o anticipo desde el 19/10/2026 no entra al libro: entra la factura que se paga con ese saldo, con su IGTF, el día en que se emite o se cobra.',
        ],
      },
      { tipo: 'nota', texto: 'Esta forma de presentar el IGTF la debe confirmar la contadora.' },
    ],
    manual: '25.1',
    relacionados: ['retenciones', 'reportes.igtf'],
    claves: ['libro de ventas', 'IGTF', 'retención', 'hoja dañada'],
  },
  {
    id: 'libros.compras',
    titulo: 'Libro de compras',
    padre: 'libros',
    resumen: 'Las facturas de los proveedores del mes, en bolívares, con el IVA y el ISLR retenidos.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué entra',
        items: [
          'Cada compra entra en el mes de su **Fecha del período fiscal**, y sale en ese orden.',
          'Columnas: fecha, RIF, proveedor, número de factura (abre la compra), control, total con IVA, exento, base, IVA, IVA retenido, número de comprobante e ISLR retenido.',
          'Lo retenido sale en el mes en que se numeró el comprobante. Una compra con comprobante provisional va con la retención en cero y la marca **retención provisional**.',
          'Si el comprobante se numeró en otro mes, lo retenido sale en ese mes como una fila propia marcada **Retención**, con la fecha de la retención dentro del mes (la misma del TXT). El comprobante impreso conserva el día en que se numeró.',
          'Un comprobante corregido lleva la marca **corregido** con su versión.',
          'Es **PRELIMINAR** mientras el mes no termine o haya compras del mes en borrador: un borrador todavía puede confirmarse y entrar.',
          'El Excel trae la columna **Fecha período fiscal**, que es la que da el orden.',
        ],
      },
      { tipo: 'nota', texto: 'Antes de declarar, numera los comprobantes provisionales en el detalle de cada compra.' },
    ],
    manual: '25.2',
    relacionados: ['compras.detalle', 'compras.declaracion'],
    claves: ['libro de compras', 'retención provisional', 'fecha fiscal'],
  },
  {
    id: 'libros.manuales',
    titulo: 'Facturas manuales',
    padre: 'libros',
    resumen: 'Las facturas hechas a mano en el talonario cuando no hubo sistema. Se registran solo para que salgan en el libro de ventas.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Arriba, la serie del talonario manual de cada sucursal.',
          'Las facturas manuales del **Período** escrito: fecha, número, control, cliente, sucursal, base, exento, IVA, total, IGTF, IVA retenido (con el número del comprobante, «del …» con su fecha y «recibido el …» si llegó otro día) y quién la registró.',
          'Las anuladas llevan la marca **ANULADA**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cargar la retención que llegó después',
        permiso: MANUALES,
        pasos: [
          'En la fila de la factura, pulsa **Cargar retención**. Sale solo en las no anuladas que todavía no tienen retención ni comprobante.',
          'La ventana dice el IVA de la factura. Escribe el **IVA retenido Bs**, el **N° comprobante**, la **Fecha del comprobante** y el día en que lo recibiste en **Recibido el** (viene con hoy).',
          'Pulsa **Cargar retención**. Sale «Retención de … cargada»: entra al libro de ventas del mes en que se recibió el comprobante.',
          'Se carga una sola vez: después ya no cambia.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Registrar una',
        permiso: MANUALES,
        pasos: ['Pulsa **Registrar factura manual**.', 'Llena la ventana: el **?** explica cada campo.', 'Pulsa **Registrar**.'],
      },
      {
        tipo: 'pasos',
        titulo: 'Anular una mal cargada',
        permiso: MANUALES,
        pasos: ['Pulsa **Anular** en su fila.', 'Escribe el **Motivo**.', 'Pulsa **Anular**. Queda en el libro en cero, con la leyenda ANULADA.'],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Una factura manual no se edita: ningún dato registrado cambia, tampoco por fuera de las pantallas, y una anulada ya no cambia más. La única excepción es **Cargar retención**, una sola vez, en la que se registró sin retención. Anularla no se deshace. El número anulado queda ocupado: el sistema no deja registrar otra del mismo tipo con ese número en esa sucursal. Si un monto quedó mal copiado, consulta con la dueña antes de anular.' },
      { tipo: 'nota', texto: 'Una factura manual no mueve inventario, caja ni cuentas por cobrar: solo entra al libro de ventas.' },
      { tipo: 'nota', texto: 'La serie del talonario manual de cada sucursal se configura en **Configuración**, en **Numeración y talonario**.' },
    ],
    mensajes: [
      { mensaje: '… ya tiene su retención (comprobante …): no se cambia', que_hacer: 'La retención de esa factura ya se cargó, y se carga una sola vez. Si está mal, consulta con la contadora.' },
      { mensaje: 'La retención de IVA no puede ser mayor que el IVA de la factura', que_hacer: 'Revisa el **IVA retenido Bs**: la ventana dice el IVA de la factura.' },
      { mensaje: 'El día en que se recibió el comprobante de retención no puede ser futuro', que_hacer: 'Corrige **Recibido el**.' },
      { mensaje: 'El comprobante de retención no se pudo recibir antes de la fecha de la factura', que_hacer: 'Corrige **Recibido el**: no puede ser antes de la factura.' },
      { mensaje: 'La fecha del comprobante de retención no puede ser anterior a la de la factura', que_hacer: 'Copia la **Fecha del comprobante** tal como está impresa en él.' },
      { mensaje: 'La fecha del comprobante de retención no puede ser posterior al día en que se recibió', que_hacer: 'Revisa la **Fecha del comprobante** y **Recibido el**.' },
      { mensaje: '… está anulada: no lleva retención', que_hacer: 'Una factura manual anulada no lleva retención.' },
    ],
    manual: '25.3',
    relacionados: ['libros.registrar-manual', 'configuracion'],
    claves: ['factura manual', 'talonario', 'sin sistema', 'serie A', 'serie B'],
  },
  {
    id: 'libros.inventario',
    titulo: 'Libro de inventario',
    padre: 'libros',
    permiso: { clave: 'productos.costos', nivel: 'lectura' },
    resumen: 'El libro que pide el artículo 177 del Reglamento de la Ley de ISLR: por producto, lo que había, lo que entró, lo que salió y lo que queda en el mes, en unidades y en bolívares. Es de toda la empresa. Hay dos: el real (todo lo físico) y el fiscal (solo lo respaldado por documentos fiscales).',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Real o fiscal',
        items: [
          'Con el selector **Real / Fiscal**, al lado del período, eliges cuál ver. Los dos tienen las mismas columnas, el mismo método y el mismo Excel.',
          '**Real**: todo lo que pasa en los almacenes, también lo que no tiene factura (compras y ventas con nota de entrega, ajustes, despieces, Cambios). Lo vendido sale cuando se entrega. Es lo que se explica abajo.',
          '**Fiscal**: solo lo respaldado por documentos fiscales. Entran las compras con factura del proveedor, las notas de crédito de facturas y el inventario inicial; salen las facturas el día en que se emiten, entregadas o no. Una factura reemitida por hoja dañada cuenta una vez.',
          'En el fiscal no entran las compras con nota de entrega, las notas de entrega a clientes, los Cambios, los ajustes (salvo el inventario inicial), los despieces, las roturas ni los traslados. **Retiros** y **Autoconsumos** salen en cero.',
          'Si se factura algo que entró sin factura, la existencia fiscal queda negativa: la fila sale en rojo con **existencia negativa** y lo que sale así no tiene valor. Al pie, por qué no coincide con el real y la tabla **Diferencia con el libro real**, producto por producto.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Una fila por producto con existencia o con movimiento en el mes, en la unidad del producto.',
          '**Unidades** y **Valores (Bs)**: existencia inicial, entradas, salidas, retiros, autoconsumos y existencia final, y el **Costo unitario**.',
          'Cada fila cuadra: inicial + entradas − salidas − retiros − autoconsumos = final, en unidades y en bolívares.',
          'El mes arranca donde cerró el anterior.',
          '**Buscar producto** filtra por código o nombre; los totales siguen siendo de todo el libro.',
          'Abajo, **De qué está hecha cada columna**: el valor de cada concepto del mes (compras, ventas entregadas, roturas…).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué va en cada columna',
        items: [
          '**Entradas**: compras, inventario inicial, devoluciones de clientes, sobrantes y las piezas de un despiece de cajas.',
          '**Salidas**: las ventas cuando se entregan, las ventas de productos con detalles, las cajas que se despiezan y las compras de otros meses que se anulan.',
          '**Retiros**: roturas, mermas, daños, pérdidas, diferencias de conteo y de traslado, láminas de vidrio rotas y bajas de productos con detalles.',
          'En **De qué está hecha cada columna** y en la hoja **Conceptos**, los retiros salen en renglones separados: **Roturas, mermas, daños y pérdidas** (también lo que faltó en un traslado) y **Diferencias de conteo y otros retiros** (lo que bajó un conteo y los ajustes con motivo **Otro**), además de las láminas rotas y las bajas. La columna **Retiros** y su total no cambian.',
          '**Autoconsumos**: los ajustes con motivo **Uso interno** y lo que se usa en la tienda de los productos con detalles.',
          'Los traslados entre tiendas y depósitos no son entradas ni salidas: lo que va en camino sigue en la existencia.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo se valora',
        items: [
          'Costo promedio ponderado de cada mes, en bolívares.',
          'Las compras entran a su costo en Bs de la factura del proveedor, el mismo monto del libro de compras, el día en que se confirman.',
          'Corregir o anular una compra después no cambia un mes cerrado: una compra de otro mes anulada sale como salida al costo promedio; una corrección de precio entra como diferencia de valor.',
          'El inventario inicial cargado por Excel entra a su costo en dólares a la tasa BCV del día de la carga.',
          'Todo lo demás entra o sale al costo promedio del mes.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Verificación y Excel',
        items: [
          'En el real, la franja verde dice que la existencia final de cada producto cuadra con el kardex de sus almacenes; en el fiscal, con la suma de sus documentos fiscales. En rojo **¡NO CUADRA!** es un error del sistema: repórtalo.',
          '**PRELIMINAR** mientras el mes no ha terminado; si no, **Mes cerrado**.',
          '**Exportar XLSX oficial** baja el libro que estás viendo con el formato de la contadora (razón social, RIF, mes y año arriba), listo para imprimir con «Página n de m» al pie, más las hojas **Conceptos** y **Método**. El fiscal trae además la columna **OBSERVACIÓN** con la existencia negativa y la hoja **Diferencia con el real**.',
        ],
      },
      { tipo: 'nota', texto: 'Lo ven la dueña y la contadora: lleva costos y es de toda la empresa. El método de valoración, y cuál de los dos libros se imprime para el artículo 177, los debe confirmar la contadora.' },
    ],
    mensajes: [
      { mensaje: '¡NO CUADRA! … producto(s) no coinciden con el kardex', que_hacer: 'Es un error del sistema: no uses ese libro y repórtalo.' },
      { mensaje: '¡NO CUADRA! … producto(s) no coinciden con sus documentos', que_hacer: 'Sale en el libro fiscal y es un error del sistema: no uses ese libro y repórtalo.' },
    ],
    manual: '25.5',
    relacionados: ['libros.compras', 'ajustes', 'reportes.inventario'],
    claves: ['libro de inventario', 'artículo 177', 'ISLR', 'autoconsumo', 'retiros', 'costo promedio', 'existencia inicial', 'existencia final', 'libro fiscal', 'inventario fiscal', 'inventario real', 'existencia negativa'],
  },
  {
    id: 'libros.registrar-manual',
    titulo: 'Registrar factura manual',
    padre: 'libros',
    permiso: MANUALES,
    resumen: 'Copia en el sistema una factura hecha a mano en el talonario, tal como está escrita y en bolívares.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar',
        pasos: [
          'Si tu usuario es global, elige la **Sucursal del talonario**. Solo salen las sucursales con serie de talonario manual.',
          'Elige el **Tipo**: Factura, Nota de crédito o Nota de débito.',
          'Escribe el **Número (sin la letra)**, el **N° de control** y la **Fecha**.',
          'Escribe el **RIF o cédula** y el **Nombre o razón social** del cliente.',
          'Escribe la **Base imponible Bs**, el **Exento Bs**, el **IVA Bs** y el **Total Bs**.',
          'Si aplica, escribe el **IGTF Bs (si pagó en divisas)**, el **IVA retenido Bs** y el **N° comprobante de retención**.',
          'Con IVA retenido sale **Comprobante recibido el (vacío = fecha de la factura)**: escribe el día en que llegó el comprobante.',
          'Sale también **Fecha del comprobante (la impresa en él)**: copia la fecha que trae el comprobante de retención. Va a la columna **Fecha comprobante** del libro.',
          'Pulsa **Registrar**. Ya aparece en el libro de ventas de su mes.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Al salir de la base, el sistema propone el IVA al 16 % y el total si están vacíos. Corrígelos si el papel dice otra cosa: mandan los del papel.',
          'La base más el exento más el IVA tiene que dar el total.',
          'El IVA retenido no puede ser mayor que el IVA de la factura.',
          'La fecha no puede ser futura.',
          'El día en que llegó el comprobante no puede ser antes de la factura ni futuro. Si es de otro mes, la retención sale en el libro de ese mes como fila **Retención**.',
          'La fecha del comprobante no puede ser antes de la factura ni después del día en que se recibió.',
          'Si el comprobante llega después de registrar la factura, no la anules: usa **Cargar retención** en su fila.',
          'Un número ya registrado, aunque esté anulado, no se registra otra vez. Revisa bien los datos antes de pulsar **Registrar**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Base (…) + exento (…) + IVA (…) no suman el total (…)', que_hacer: 'Copia los montos tal como están en el papel y revisa la suma.' },
      { mensaje: 'La retención de IVA no puede ser mayor que el IVA de la factura', que_hacer: 'Revisa el **IVA retenido Bs**.' },
      { mensaje: 'La fecha de la factura no puede ser futura', que_hacer: 'Revisa la **Fecha**.' },
      { mensaje: 'El comprobante de retención no se pudo recibir antes de la fecha de la factura', que_hacer: 'Revisa **Comprobante recibido el (vacío = fecha de la factura)** y la **Fecha**.' },
      { mensaje: 'El día en que se recibió el comprobante de retención no puede ser futuro', que_hacer: 'Corrige **Comprobante recibido el (vacío = fecha de la factura)**.' },
      { mensaje: 'La fecha del comprobante de retención no puede ser anterior a la de la factura', que_hacer: 'Copia la **Fecha del comprobante (la impresa en él)** tal como está en el papel.' },
      { mensaje: 'La fecha del comprobante de retención no puede ser posterior al día en que se recibió', que_hacer: 'Revisa la **Fecha del comprobante (la impresa en él)** y **Comprobante recibido el**.' },
      { mensaje: 'La factura manual … ya está registrada', que_hacer: 'Ese número ya está en la lista, también si se anuló: búscala en su mes. No se registra dos veces.' },
      { mensaje: 'Esta sucursal no tiene configurada la serie de su talonario manual (…)', que_hacer: 'La dueña la configura en **Configuración**, en **Numeración y talonario**.' },
      { mensaje: 'Indica la sucursal del talonario', que_hacer: 'Elige la **Sucursal del talonario**.' },
    ],
    manual: '25.3',
    claves: ['registrar factura manual', 'talonario', 'IVA 16'],
  },
];
